/**
 * The subscribe flow: age tiers, plans, extras, receipt and checkout.
 *
 * Lives here rather than inline in sections/subscribe-flow.liquid because that
 * file crossed Shopify's 256 KB template limit on 3 Sep 2026 and the GitHub sync
 * silently stopped updating it. Nothing in here needs Liquid: every value the
 * section knows is handed over in the [data-flow-config] JSON block.
 *
 * Runs once per flow root. The `initialized` flag mirrors what the inline
 * script did, so a section re-render in the editor does not bind twice.
 */
document.querySelectorAll('[data-subscribe-flow]').forEach((root) => {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';

  const config = JSON.parse(root.querySelector('[data-flow-config]').textContent);

  // Display order: anchor high, 12-month preselected.
  // name = big first line on the card; sub = smaller line below it.
  // label = full name, used in the order summary line.
  const TERMS = [
    { key: '12mo', months: 12, match: /12-month/i, name: '12-Month', sub: 'Prepaid Subscription', label: '12-Month Prepaid Subscription', badge: 'Best Value' },
    { key: '6mo', months: 6, match: /6-month/i, name: '6-Month', sub: 'Prepaid Subscription', label: '6-Month Prepaid Subscription', badge: null },
    { key: '3mo', months: 3, match: /3-month/i, name: '3-Month', sub: 'Prepaid Subscription', label: '3-Month Prepaid Subscription', badge: null },
    { key: 'monthly', months: 1, match: /^monthly/i, name: 'Monthly', sub: 'Subscription', label: 'Monthly Subscription', badge: null },
  ];
  // Show the "$X billed today" line on plan cards. Off for now — flip to
  // true to bring it back.
  const SHOW_BILLED_TODAY = false;
  // Tap-to-enlarge on the free past-box thumbnails. Off: no trigger is
  // rendered, so no magnifier badge, no cursor change and nothing for the
  // dialog's listeners to match. The dialog, its styles and the large
  // image URLs are all still here — flip this to true to bring it back.
  const ENABLE_PHOTO_ZOOM = false;

  // A/B arm. The Rollouts experiment flips the theme setting behind
  // config.promoBucket; treat anything but an exact "B" as the control, so
  // a missing or misspelt value can only ever land on today's behaviour.
  const BUCKET = config.promoBucket === 'B' ? 'B' : 'A';
  // The arm is only stamped on the cart while a promo split test is running
  // (Theme settings → Promos). Outside a test every order would carry the same
  // value, which reads as a test that never ended.
  const PROMO_TEST_RUNNING = document.body.dataset.promoTest === 'running';

  // `explorers` is the roster — one entry per subscription in this
  // checkout, each with its own age tier and (when asked) the child's
  // name. Entry 0 is the one the steps themselves configure, and
  // `ageVariantId`/`ageTitle` mirror it: every tier-shaped thing on the
  // page (the gallery, the plan cards' prices, the gift picture) speaks
  // for the explorer being configured, which during the steps is always
  // entry 0. Explorers 2+ arrive from the sheet on the review step.
  //
  // `removed` holds a row pulled from the roster that can still be put
  // back — see the undo strip in updateSummary.
  const state = {
    ageVariantId: null,
    ageTitle: '',
    // Tier whose gallery is showing: the box added most recently.
    galleryTierIndex: null,
    term: '12mo',
    kids: 1,
    addons: new Map(),
    step: 1,
    explorers: [],
    removed: null,
    // Explorer uid → variant id of the vest size picked for that explorer.
    vestSizes: new Map(),
  };
  let boxData = null;
  // Assigned by the roster block below, called once live prices land. A
  // `let` here rather than a call into that block, so the hoisting order
  // between the two is explicit instead of accidental.
  let rosterRepaint = () => {};

  const MAX_EXPLORERS = Math.max(1, Number(config.maxExplorers) || 1);

  // ---------- The roster ----------
  // Several children in one checkout: N subscription lines instead of one,
  // plan cards priced as a family total, gift units counted across the
  // roster. It won the multiplan-2 A/B test on 19 Oct 2026 and is no longer
  // gated on a bucket. `max_explorers` at 1 is the off switch: the age cards
  // go back to a pick-one group with no quantity control.
  const rosterOn = () => MAX_EXPLORERS > 1;
  const effectiveMax = () => (rosterOn() ? MAX_EXPLORERS : 1);

  // Family discount. Percent off for a roster of n boxes, from the three
  // section settings — 2 boxes, 3 boxes, 4-or-more. Nothing below charges
  // it; Recharge does, from its own tiered discount on the box product.
  // The rate is advertised here and matched there by hand.
  const FAMILY_PERCENTS = Array.isArray(config.familyPercents) ? config.familyPercents : [];
  const FAMILY_COPY = config.familyCopy || {};
  // Recharge applies the multibox rate to each subscription line, not to the
  // order, and truncates that line's discount to the cent: 5% of $132.57 is
  // $6.6285 and comes off as $6.62. Taking the percentage off the summed total
  // and rounding once put this page a cent or two under the amount the card was
  // actually charged on every multi-box order (2 x 3-month read $251.88 against
  // a $251.90 charge). Every box is its own cart line at quantity 1, so mirror
  // it line by line.
  function familyOffFor(prices, pct) {
    if (!(pct > 0)) return 0;
    return prices.reduce((sum, price) => sum + Math.floor((price * pct) / 100), 0);
  }

  function familyPercentFor(n) {
    if (!rosterOn() || n < 2) return 0;
    const pct = Number(FAMILY_PERCENTS[Math.min(n, 4) - 2]);
    return Number.isFinite(pct) && pct > 0 ? pct : 0;
  }
  let explorerUid = 0;
  function newExplorer(fields) {
    explorerUid += 1;
    return { uid: explorerUid, variantId: null, title: '', tierIndex: '', ...fields };
  }

  const primaryExplorer = () => state.explorers[0];

  // ---------- Funnel analytics ----------
  // This page replaced the product page as the buy path on 31 Jul 2026.
  // ViewContent only fires on product templates, so the move silently cut
  // the one event between PageView and AddToCart: site-wide ViewContent
  // fell from ~2,400/day to ~500/day with no matching drop anywhere else,
  // which left the whole selection funnel unmeasured. Two events restore it:
  //
  //   ViewContent   — once per load, standing in for the product page.
  //   SubscribeStep — custom, once per step advance, so drop-off between
  //                   age → plan → extras is visible per step.
  //
  // AddToCart and InitiateCheckout are deliberately NOT sent here. Shopify's
  // own web pixel still emits both from real cart and checkout events, and a
  // second copy would inflate them against a ViewContent that has no
  // duplicate — which is precisely the kind of skew that made the August
  // funnel unreadable.
  //
  // Everything is routed through dataLayer as well as fbq: the Meta pixel is
  // loaded by GTM (container GTM-K8MSVWZ, served first-party via
  // fine.edzoocation.com), so dataLayer is the supported seam and works for
  // GA4 and any future tag without touching this file again.
  const analytics = (() => {
    const STEP_NAMES = new Map([[1, 'age'], [2, 'plan']]);
    if (config.siblingStep) STEP_NAMES.set(config.siblingStep, 'siblings');
    if (config.flashboxStep) STEP_NAMES.set(config.flashboxStep, 'flashbox');
    STEP_NAMES.set(config.lastStep, 'extras');

    // fbq is created by the GTM template, not by a snippet in this theme, so
    // it may not exist when the first event fires. Hold events until it does,
    // then flush in order. Bounded so a page where GTM never loads (consent
    // declined, blocker) drops the queue instead of retrying forever.
    const pending = [];
    let flushTimer = null;
    let waited = 0;

    function flush() {
      if (typeof window.fbq !== 'function') {
        waited += 400;
        if (waited >= 8000) { pending.length = 0; flushTimer = null; return; }
        return;
      }
      clearInterval(flushTimer);
      flushTimer = null;
      while (pending.length) {
        const [kind, name, params] = pending.shift();
        try { window.fbq(kind, name, params); } catch (e) { /* never block the flow */ }
      }
    }

    function send(kind, name, params) {
      pending.push([kind, name, params]);
      if (typeof window.fbq === 'function') { flush(); return; }
      if (!flushTimer) flushTimer = setInterval(flush, 400);
    }

    let viewed = false;
    return {
      // The step's stable name, exposed so nothing else keeps a second copy
      // of these strings. Numbers are computed at render from which optional
      // steps are enabled, so they move between configurations; these don't.
      stepName: (n) => STEP_NAMES.get(n) || String(n),
      viewContent() {
        if (viewed) return;
        viewed = true;
        const params = {
          content_type: 'product',
          content_name: 'edZOOcation Subscription Box',
          currency: config.currency,
          // A/B arm, so GA4 and Meta can segment the funnel the same way
          // the order attribution does.
          bucket: BUCKET,
        };
        if (config.boxProductId) params.content_ids = [String(config.boxProductId)];
        // boxData carries the real price once loadProduct resolves;
        // fire without a value rather than not at all if that fetch failed.
        if (boxData && typeof boxData.price === 'number') params.value = boxData.price / 100;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'subscribe_view_content', ...params });
        send('track', 'ViewContent', params);
      },
      // firstReach distinguishes a step the shopper is seeing for the first
      // time from a re-entry after tapping Back. Both are real step views and
      // both are sent, but a funnel built on all of them counts re-entries as
      // fresh arrivals and understates drop-off. Filter on first_reach for
      // stage-to-stage rates; use everything for navigation behaviour.
      step(n, firstReach) {
        const params = {
          step: n,
          step_name: STEP_NAMES.get(n) || String(n),
          total_steps: config.lastStep,
          first_reach: !!firstReach,
          term: state.term,
          age: state.ageTitle || null,
          // How many subscriptions the shopper is carrying at this step.
          // The funnel is read per-step, so without this a multi-explorer
          // checkout is indistinguishable from a single one.
          explorers: state.explorers.length,
          bucket: BUCKET,
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'subscribe_step', ...params });
        send('trackCustom', 'SubscribeStep', params);
      },
      // Roster changes on the review step. Sent as its own event because
      // it happens after the last step event, so nothing else would
      // record that a second subscription was ever considered.
      explorers(action) {
        const params = {
          action,
          explorers: state.explorers.length,
          term: state.term,
          bucket: BUCKET,
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'subscribe_explorers', ...params });
        send('trackCustom', 'SubscribeExplorers', params);
      },
    };
  })();

  // ---------- Free-gift promo ----------
  // The promo app owns the schedule and the gift variant; the theme only
  // owns how it looks (the gift_product setting). Read the app's embedded
  // config rather than hardcoding dates, so the offer stops advertising
  // itself the moment its window closes instead of promising a gift the
  // discount no longer honors until someone blanks the setting by hand.
  function bxgyConfig() {
    // School-channel visitors (snippets/school-channel) are offered no gift:
    // ClassWallet bills the gift lines at full price. No promo here hides
    // every gift panel, chip, receipt line and the vest, the same as a
    // market with no promo.
    if (window.EdzSchoolChannel) return null;
    const el = document.getElementById('bxgy-config');
    if (!el || !el.textContent) return null;
    try {
      const app = JSON.parse(el.textContent);
      if (!app) return null;
      // Promos are published per market. Three shapes can land here:
      // a v2 config with a `promos` list (each naming its countries), a
      // single already-resolved promo carrying its own `markets` (the
      // app embed resolves server-side), or a legacy v1 root promo with
      // no market field at all — which predates markets and was only
      // ever offered (and fulfillable) in the US, so it maps to US-only
      // rather than everywhere. No promo for this country means no
      // gift: advertising or adding one checkout will refuse is the bug
      // this gate exists to stop.
      const promos = Array.isArray(app.promos)
        ? app.promos
        : [{ ...app, markets: Array.isArray(app.markets) && app.markets.length ? app.markets : ['US'] }];
      // Resolving the promo is all this does. Whether it carries anything
      // to give away is the offers' question, not this one: a config that
      // names no `productYVariantId` still has a bucket B offer to read,
      // and demanding the control's gift here would have hidden it.
      const promo = promos.find((p) => Array.isArray(p.markets) && p.markets.includes(config.country));
      return promo || null;
    } catch (e) {
      return null;
    }
  }

  // Read the same two absolute instants the promo app writes to its Shopify
  // discount. The app's startDateTime/endDateTime are naive wall clock, and
  // `new Date` resolves a string with no offset in the *browser's* timezone —
  // so gating on those moved the window per customer, and a shopper west of
  // the store could be shown a free gift the discount had already stopped
  // honouring. No window means no promo; there is no fallback to the naive
  // fields, because promising a gift nothing will discount is the bug.
  // Gated on absolute UTC instants only; see the note above.
  function promoWindowOpen(app) {
    if (!app.startsAtUtc || !app.endsAtUtc) return false;
    const now = Date.now();
    return now >= Date.parse(app.startsAtUtc) && now <= Date.parse(app.endsAtUtc);
  }

  // ---------- Bucket offers ----------
  // Both arms of the A/B test normalize to one shape:
  //   { qualifyingSellingPlanIds: string[],
  //     giftSetsByXVariant: { [xGid]: { take, candidates: string[], candidateHandles } } }
  // An empty plan list means every plan qualifies. Bucket A is the single
  // gift mapped onto every qualifying box variant; bucket B is the past-box
  // set the config carries under bucketOverrides.B. `candidates` are bare
  // variant gids — the only thing that decides what is given away — and
  // `candidateHandles` is display data the flow uses to look each box up.
  //
  // The function below is a copy of the canonical one, which lives in the
  // promo app at extensions/bxgy-discount/src/apply-bucket.js and is shared
  // by the discount Function and the cart embed. It is copied verbatim,
  // double quotes and all, so the two can be diffed by eye; edit it there
  // first and bring the change over unchanged. Deliberately strict: bucket
  // B returns null outright on anything malformed rather than reading a
  // typo generously, because the generous reading of a bad `take` is a cart
  // full of free boxes. Nothing here authorizes a discount — the Function
  // decides what is actually free — so a bucket the server disagrees with
  // shows a wrong picture, never a wrong charge.
  function bucketOffer(promo, bucket) {
    if (!promo || typeof promo !== "object" || Array.isArray(promo)) {
      return null;
    }

    const isObject = (value) =>
      Boolean(value) && typeof value === "object" && !Array.isArray(value);
    const isGid = (value) => typeof value === "string" && value.length > 0;
    const isGidList = (value) => Array.isArray(value) && value.every(isGid);

    // Candidates come in two shapes -- a bare gid string, and a { gid, handle }
    // object -- so a config written before handles existed keeps working.
    const candidateGid = (entry) => {
      if (isGid(entry)) return entry;
      if (isObject(entry) && isGid(entry.gid)) return entry.gid;
      return null;
    };
    const candidateHandle = (entry) =>
      isObject(entry) && isGid(entry.handle) ? entry.handle : null;

    if (bucket !== "B") {
      // A: the promo's own fields, reshaped. One gift, every qualifying box.
      if (!promo.productYVariantId) return null;

      const giftSetsByXVariant = {};
      const xVariantIds = Array.isArray(promo.productXVariantIds)
        ? promo.productXVariantIds
        : [];
      for (const xVariantId of xVariantIds) {
        giftSetsByXVariant[xVariantId] = {
          take: 1,
          candidates: [promo.productYVariantId],
          candidateHandles: {},
          choices: [],
        };
      }

      return {
        qualifyingSellingPlanIds: Array.isArray(promo.qualifyingSellingPlanIds)
          ? promo.qualifyingSellingPlanIds
          : [],
        giftSetsByXVariant,
      };
    }

    const override = isObject(promo.bucketOverrides)
      ? promo.bucketOverrides.B
      : null;
    if (!isObject(override)) return null;
    if (!isGidList(override.qualifyingSellingPlanIds)) return null;
    if (!isObject(override.giftSetsByXVariant)) return null;

    const xVariantIds = Object.keys(override.giftSetsByXVariant);
    if (xVariantIds.length === 0) return null;

    const giftSetsByXVariant = {};
    for (const xVariantId of xVariantIds) {
      const set = override.giftSetsByXVariant[xVariantId];
      if (!isObject(set)) return null;
      if (!Array.isArray(set.candidates) || set.candidates.length === 0) {
        return null;
      }

      const candidates = [];
      const candidateHandles = {};
      const choices = [];
      for (const entry of set.candidates) {
        // A slot is any object carrying `choice`. One that also names a `gid`
        // is ambiguous -- a box or a pick -- and is refused rather than read
        // either way.
        if (isObject(entry) && "choice" in entry) {
          if ("gid" in entry) return null;
          if (!isGidList(entry.choice) || entry.choice.length === 0) return null;
          choices.push({
            variants: entry.choice.slice(),
            handle: candidateHandle(entry),
          });
          continue;
        }

        const gid = candidateGid(entry);
        if (!gid) return null;
        candidates.push(gid);
        const handle = candidateHandle(entry);
        if (handle) candidateHandles[gid] = handle;
      }

      // Every slot takes one unit of `take`, so `take` can be no smaller than
      // the number of slots and no larger than everything the list can give.
      if (!Number.isInteger(set.take)) return null;
      if (set.take < Math.max(1, choices.length)) return null;
      if (set.take > candidates.length + choices.length) return null;

      giftSetsByXVariant[xVariantId] = {
        take: set.take,
        candidates,
        candidateHandles,
        choices,
      };
    }

    return {
      qualifyingSellingPlanIds: override.qualifyingSellingPlanIds.slice(),
      giftSetsByXVariant,
    };
  }

  // Whichever offer the config actually carries. Bucket B falls back to A
  // whenever the overrides are missing or unreadable, so a half-written
  // override shows the control gift rather than nothing at all — and A
  // being absent in turn (a promo that names no gift product, which a B-only
  // config legitimately does) is not fatal either. Only both coming back
  // null means there is nothing to offer.
  function effectiveOffer(app) {
    const promo = app || bxgyConfig();
    if (!promo) return null;
    if (BUCKET === 'B') {
      const offerB = bucketOffer(promo, 'B');
      if (offerB) return offerB;
    }
    return bucketOffer(promo, 'A');
  }

  // True only when the shopper is actually being shown the past-box bundle:
  // bucket B *and* a config that describes one.
  function bundleMode(app) {
    if (BUCKET !== 'B') return false;
    return !!bucketOffer(app || bxgyConfig(), 'B');
  }

  // The one gate on whether a plan earns the gift, for both arms. It used to
  // be a minimum prepaid term set in the section settings, which the cart
  // side never consulted — so the two could disagree. The offer's own plan
  // list is what the discount honors, so it is what the flow advertises.
  function planQualifies(planId, offer) {
    const active = offer || effectiveOffer();
    if (!active || !planId) return false;
    const plans = active.qualifyingSellingPlanIds;
    return !plans.length || plans.includes(`gid://shopify/SellingPlan/${planId}`);
  }

  function giftSetFor(offer, variantId) {
    if (!offer || !variantId) return null;
    return offer.giftSetsByXVariant[`gid://shopify/ProductVariant/${variantId}`] || null;
  }

  // Trailing numeric id of any Shopify gid — variants and selling plans
  // alike; the cart and the /products JSON both speak in bare numbers.
  const idFromGid = (gid) => Number((String(gid).match(/(\d+)$/) || [])[1]) || null;

  // ---------- Free collector vest (12-month plans) ----------
  // The shopper picks the size; nothing infers it from the box tier, since
  // kids in one age band span every size. Every 12-month box earns one vest,
  // so a roster of several explorers picks one size per explorer.
  //
  // The vest rides in the promo's bucket B gift set as a choice slot — one
  // unit of `take` that any of its sizes fills — which is what makes it
  // free. The promo app never adds a slot item itself; the chosen size
  // arrives from here. Slots are normalised into `set.choices`, apart from
  // the plain `candidates`, so every gift-set walk below covers the boxes
  // only. The vest-size filter is a second guard in case a size is ever
  // listed as a plain candidate.
  //
  // Challenger arm of the `fieldvest` A/B test only. The framework assigns
  // buckets in the head, before this script runs, so the attribute is already
  // set; a visitor in control, or on a page where the test isn't running,
  // has no `challenger` and gets the flow exactly as it was — no picker, no
  // vest row on the 12-month card, no vest line in the cart.
  const VEST_ARM = document.documentElement.getAttribute('data-ab-fieldvest') === 'challenger';
  const VEST =
    VEST_ARM && config.vest && Array.isArray(config.vest.variants) && config.vest.variants.length ? config.vest : null;
  const VEST_GIDS = new Set(VEST ? VEST.variants.map((v) => `gid://shopify/ProductVariant/${v.id}`) : []);
  const isVestGid = (gid) => VEST_GIDS.has(gid);
  // The units of a gift set's `take` left for boxes once its slots are served.
  const boxTake = (set) => set.take - (Array.isArray(set.choices) ? set.choices.length : 0);
  const vestEl = root.querySelector('[data-vest]');
  const vestPromptEl = root.querySelector('[data-vest-prompt]');

  // A plan earns the vest when it is a 12-month plan the promo qualifies —
  // the same gate the discount applies to the gift set the vest rides in.
  function vestEarnedBy(alloc) {
    if (!VEST || !alloc || state.term !== '12mo') return false;
    const app = bxgyConfig();
    return giftOffered(app) && planQualifies(alloc.planId, effectiveOffer(app));
  }
  // The explorers whose box earns a vest, in roster order.
  function vestExplorers() {
    return VEST ? readyExplorers().filter((x) => vestEarnedBy(allocFor(x))) : [];
  }
  function vestCount() {
    return vestExplorers().length;
  }
  const vestVariantById = (id) => (VEST && id ? VEST.variants.find((v) => String(v.id) === String(id)) || null : null);
  const vestMissing = () => vestExplorers().some((x) => !vestVariantById(state.vestSizes.get(x.uid)));
  // The picked sizes as cart lines: one per size, its quantity the number of
  // explorers who picked it.
  function vestLines() {
    const lines = new Map();
    vestExplorers().forEach((x) => {
      const variant = vestVariantById(state.vestSizes.get(x.uid));
      if (!variant) return;
      const line = lines.get(variant.id) || { variant, qty: 0 };
      line.qty += 1;
      lines.set(variant.id, line);
    });
    return [...lines.values()];
  }
  function setVestPrompt(text) {
    if (vestPromptEl) vestPromptEl.textContent = text;
  }
  // Replays one of the box's animations from the start, even mid-play.
  function playVest(cls) {
    if (!vestEl) return;
    vestEl.classList.remove('is-entering', 'is-prompting');
    void vestEl.offsetWidth;
    vestEl.classList.add(cls);
    clearTimeout(playVest.timer);
    playVest.timer = setTimeout(() => vestEl.classList.remove(cls), 1800);
  }
  // Continue or Checkout tapped with a size missing: back up to the sizes,
  // scrolled to the first explorer without one, and nudge the box.
  function promptVest() {
    if (!vestEl) return;
    setVestPrompt(vestCount() > 1 ? 'Pick a vest size for each explorer.' : 'Pick a vest size first.');
    const missing = vestExplorers().find((x) => !vestVariantById(state.vestSizes.get(x.uid)));
    const target = (missing && vestEl.querySelector(`[data-vest-child][data-uid="${missing.uid}"]`)) || vestEl;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    playVest('is-prompting');
  }

  // One block of sizes per explorer whose box earns a vest. The Liquid block
  // is the template; extra explorers get clones with their own radio group,
  // labelled with their tier. Rebuilt only when the set of explorers changes,
  // and every pick lives in `state.vestSizes`, so nothing is lost when the
  // roster grows or shrinks.
  const vestRowsEl = vestEl && vestEl.querySelector('[data-vest-rows]');
  const vestTemplate = vestRowsEl && vestRowsEl.querySelector('[data-vest-child]');
  let vestRowsKey = '';
  function renderVestRows() {
    if (!vestRowsEl || !vestTemplate) return;
    const explorers = vestExplorers();
    // Forget picks for explorers that have left the roster.
    const live = new Set(explorers.map((x) => x.uid));
    [...state.vestSizes.keys()].forEach((uid) => {
      if (!live.has(uid)) state.vestSizes.delete(uid);
    });
    const key = explorers.map((x) => `${x.uid}:${x.title}`).join('|');
    if (key === vestRowsKey) return;
    const shown = new Set([...vestRowsEl.querySelectorAll('[data-vest-child]')].map((el) => el.dataset.uid));
    const firstRender = vestRowsKey === '';
    vestRowsKey = key;
    const multiple = explorers.length > 1;
    const blocks = explorers.map((x, index) => {
      const block = index === 0 ? vestTemplate : vestTemplate.cloneNode(true);
      block.dataset.uid = String(x.uid);
      // A block for an explorer who just joined slides in; the rest stay put.
      block.classList.toggle('is-new', !firstRender && !shown.has(String(x.uid)));
      const label = block.querySelector('[data-vest-child-label]');
      const labelId = `sf-vest-child-${x.uid}`;
      label.hidden = !multiple;
      label.id = labelId;
      if (multiple) {
        // Explorer number first and bold; the tier quieter beside it.
        const who = document.createElement('span');
        who.className = 'sf__vest-child-who';
        who.textContent = `Explorer ${index + 1}`;
        const tier = document.createElement('span');
        tier.className = 'sf__vest-child-tier';
        tier.textContent = ` · ${(x.title || '').replace(/\s*\(.*\)\s*$/, '')}`;
        label.replaceChildren(who, tier);
      } else {
        label.replaceChildren();
      }
      const row = block.querySelector('.sf__vest-row');
      row.setAttribute('role', 'radiogroup');
      if (multiple) row.setAttribute('aria-labelledby', labelId);
      else row.removeAttribute('aria-labelledby');
      const picked = String(state.vestSizes.get(x.uid) || '');
      block.querySelectorAll('[data-vest-size]').forEach((input) => {
        input.name = `sf-vest-size-${x.uid}`;
        input.checked = input.value === picked;
      });
      return block;
    });
    if (blocks.length) vestRowsEl.replaceChildren(...blocks);
  }

  // The Explorer Kit leads the add-ons — except while the free vest is on
  // offer, because the kit holds a vest too. Then it moves to the fourth
  // card, behind the first three of the collection, and back again when the
  // vest is no longer offered. A real DOM move, so keyboard order follows
  // what the shopper sees; animated when the grid is on screen.
  const addonsGridEl = root.querySelector('[data-addon-pinned]')?.parentElement || null;
  let kitBehind = false;
  function placeKit() {
    if (!addonsGridEl) return;
    const kit = addonsGridEl.querySelector(':scope > [data-addon-pinned]');
    if (!kit) return;
    const behind = vestCount() > 0;
    if (behind === kitBehind) return;
    kitBehind = behind;
    const cells = [...addonsGridEl.children];
    const animate = addonsGridEl.offsetParent !== null && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const before = animate ? new Map(cells.map((cell) => [cell, cell.getBoundingClientRect()])) : null;
    if (behind) {
      const others = cells.filter((cell) => cell !== kit);
      addonsGridEl.insertBefore(kit, others[3] || null);
    } else {
      addonsGridEl.insertBefore(kit, addonsGridEl.firstElementChild);
    }
    if (!before) return;
    cells.forEach((cell) => {
      const from = before.get(cell);
      const to = cell.getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      if (!dx && !dy) return;
      cell.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
        duration: 380,
        easing: 'cubic-bezier(0.3, 1.1, 0.5, 1)',
      });
    });
  }

  // Opens the picker (top of the Extras step) while the selected plan earns
  // the vest, closes it otherwise, keeps one block of sizes per explorer and
  // places the kit. Called from updateSummary, which every plan, roster and
  // step change already runs through.
  function paintVest() {
    placeKit();
    if (!vestEl) return;
    renderVestRows();
    const open = vestCount() > 0;
    if (open === vestEl.classList.contains('is-open')) return;
    vestEl.classList.toggle('is-open', open);
    vestEl.inert = !open;
    if (!open) setVestPrompt('');
  }
  if (vestEl) {
    // Delegated, so cloned blocks need no listeners of their own.
    vestEl.addEventListener('change', (event) => {
      const input = event.target.closest('[data-vest-size]');
      if (!input || !input.checked) return;
      const block = input.closest('[data-vest-child]');
      const uid = block ? Number(block.dataset.uid) : 0;
      if (!uid) return;
      state.vestSizes.set(uid, input.value);
      if (!vestMissing()) setVestPrompt('');
      updateContinueUI();
      updateSummary();
    });
  }

  // Display gate only. The cart-side safety net below deliberately does
  // not consult the gift_product setting — the app still adds the gift
  // whether or not the theme is set up to picture it. Bucket B pictures the
  // bundle from its own pool, so it doesn't consult that setting either: a
  // sold-out spider must not take the past boxes down with it.
  function giftOffered(app) {
    const promo = app || bxgyConfig();
    // The preview theme's "show promos outside their dates" setting opens the
    // window here and only here: this decides what is pictured. The fallback
    // adder keeps the real window, as the promo app and the discount do, so a
    // promo previewed early is never put in a cart.
    const shown = promoWindowOpen(promo || {}) || config.promoPreviewForce === true;
    if (!promo || !shown) return false;
    if (bundleMode(promo)) return true;
    return !!config.giftTitle;
  }

  const esc = (s) => { const d = document.createElement('span'); d.textContent = s; return d.innerHTML; };

  // A free past box's name ends in the age tier it was made for — "Bee
  // Zookeeper Box (Ages 3-5)". The tier always goes on its own line, so
  // three stacked rows read as three animals rather than three repeats of
  // the tier the shopper already picked. Only these rows: every other name
  // on the page stays on one line.
  //
  // Anchored to the end of the name on purpose: a name with the tier in the
  // middle ("Spots and Scales Bundle (ages 6-8) - Komodo Dragon Plush")
  // reads worse split than left alone. "Age"/"Ages", either dash, any case.
  const AGE_SUFFIX = /\s*(\(ages?\b[^)]*\d[^)]*\))\s*$/i;
  const escPastBoxName = (s) => {
    const str = String(s == null ? '' : s);
    const match = str.match(AGE_SUFFIX);
    if (!match) return esc(str);
    return `${esc(str.slice(0, match.index))}<span class="sf__name-age">${esc(match[1])}</span>`;
  };

  // The bundle subheading is a merchant-editable text setting, so it cannot
  // carry markup. `**like this**` bolds a run of it — applied after escaping,
  // so the setting still cannot inject HTML.
  const BOLD_RUN = /\*\*([^*]+)\*\*/g;
  const escBold = (s) => esc(String(s == null ? '' : s)).replace(BOLD_RUN, '<b>$1</b>');

  // The bundle's name and subheading are written once for every market, and
  // the markets give different numbers of boxes — three in the US, one or two
  // in Canada. `[count]` becomes the number of boxes pictured: the set shown,
  // not the config's `take`, so a tier that has run short is described as it
  // is. A one-box set takes its own wording, since "1-Box … Bundle" reads as
  // a typo.
  const bundleCopy = (text, one, count) =>
    String((count === 1 && one) || text || '').replace(/\[count\]/g, String(count));

  // ---------- Bucket B bundle display ----------
  // How each free box is pictured. There is no theme setting to fill in and
  // nothing to keep in step with the promo config: the config names its
  // candidates by handle, and every box's name, photo, tier price and stock
  // are read live (loadProduct) as the flow loads. Fresh by
  // construction — a page served from cache can outlive the stock it names,
  // and a fetch cannot be cached the way the rendered page can.
  //
  // Keyed by variant gid, and only in-stock variants go in: each tier of a
  // past box has its own stock, and the tier the shopper picked is the only
  // one that can be given to them. A candidate missing from this map — its
  // product wouldn't load, or that tier has sold out — is skipped outright
  // rather than shown as a nameless row, so the next candidate in the
  // config's order takes its place.
  const candidateDisplay = new Map();
  // Whether loadCandidateDisplay has finished. The map fills one product
  // fetch at a time, so `candidateDisplay.size > 0` is true the moment the
  // FIRST tier resolves — while a later tier's fetch is still in flight. A
  // checkout reached in that window read the half-filled map as complete and
  // added nothing for the unresolved tier (order #187015, 5 Sep 2026: the
  // Zookeeper set shipped, the Zoologist set silently didn't). Gate on
  // "loaded", not "non-empty", so a partial map falls back to the raw
  // candidate list instead of being mistaken for a finished one.
  let candidateDisplayReady = false;

  // Product image URLs are full CDN URLs, sized by query parameter.
  // Every rendition is built from the raw URL: appending a second `width`
  // to an already-sized one leaves the CDN two widths to pick between.
  const sizedImage = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : null);

  async function loadCandidateDisplay(offer) {
    const handles = new Set();
    Object.values(offer.giftSetsByXVariant).forEach((set) => {
      Object.values(set.candidateHandles || {}).forEach((handle) => handles.add(handle));
    });
    await Promise.all(
      [...handles].map(async (handle) => {
        try {
          // One attempt: a draft or archived candidate 404s, and is skipped.
          const product = await loadProduct(handle, 1);
          // `product.featured_image` is a URL string; a variant's own
          // `featured_image` is an object.
          const productImage = product.featured_image || (product.images || [])[0] || null;
          (product.variants || []).forEach((variant) => {
            if (!variant.available) return;
            const source = (variant.featured_image && variant.featured_image.src) || productImage;
            candidateDisplay.set(`gid://shopify/ProductVariant/${variant.id}`, {
              // The product name is the animal the shopper recognises; the
              // variant title is only the age tier they already chose.
              name: product.title,
              price: variant.price,
              image: sizedImage(source, 160),
              // Loaded only when a thumbnail is tapped. 1200 covers the
              // dialog on a phone at 3x and on a desktop with room over.
              imageLarge: sizedImage(source, 1200),
            });
          });
        } catch (e) {
          /* unresolved candidate — skipped, never shown half-known */
        }
      })
    );
    candidateDisplayReady = true;
  }

  // The variant gids this tier is actually being offered: the first `take`
  // candidates that resolved to a box that can be given away, in the
  // config's order. One list, read by both the panel that pictures the
  // bundle and the net that adds it, so a shopper is never shown one set of
  // boxes and given another. Returns fewer when the list has thinned, so
  // the bundle degrades to two boxes or one instead of blocking the flow.
  //
  // `null` means there is no display data at all — bucket A, which carries
  // no handles, or a page whose product fetches haven't landed yet — and is
  // the caller's cue to fall back to the raw candidate order. An empty
  // array is a different answer and a deliberate one: display data exists
  // and this tier resolved no box from it, so nothing is shown and nothing
  // is added. Don't collapse the two.
  function displayedGiftGids(set) {
    if (!set || !candidateDisplayReady) return null;
    const gids = [];
    const limit = boxTake(set);
    for (const gid of set.candidates) {
      if (gids.length >= limit) break;
      if (isVestGid(gid)) continue;
      if (candidateDisplay.has(gid)) gids.push(gid);
    }
    return gids;
  }

  function tierBundleRows(offer, variantId) {
    const gids = displayedGiftGids(giftSetFor(offer || effectiveOffer(), variantId || state.ageVariantId));
    // The gid rides along on the row so a rendered thumbnail can find its
    // way back to this record. The enlarge dialog then reads the name,
    // price and large image straight off the map instead of parsing them
    // back out of markup — nothing has to be escaped into an attribute.
    return gids ? gids.map((gid) => ({ gid, ...candidateDisplay.get(gid) })) : [];
  }

  const bundleValueOf = (rows) => rows.reduce((sum, row) => sum + (row.price || 0), 0);

  // Every price on this step is rendered here: plan cards, per-box price,
  // savings, the gift value and the order summary. The amounts already
  // arrive in the market's currency — Liquid `money` values and the
  // Storefront API allocations are both presentment — so `narrowSymbol`
  // renders a plain "$" for USD and CAD alike. No ISO suffix: Canadian
  // shoppers on a .ca storefront read "$" as CAD, the local convention,
  // and "$56.99 CAD" looked foreign rather than clearer.
  const moneyFormatter = new Intl.NumberFormat(config.locale || 'en', {
    style: 'currency',
    currency: config.currency,
    currencyDisplay: 'narrowSymbol',
  });
  const formatMoney = (cents) => moneyFormatter.format(cents / 100);

  // ---------- Enlarged box photo ----------
  // A free box is pictured at 44px on the plan card and 52px in the cart,
  // which is too small to tell one month's animal from another's. Tapping
  // a thumbnail opens the same photo full size.
  //
  // A native <dialog> shown with showModal(), not the size-chart popover
  // this section uses elsewhere: a modal dialog makes the page behind it
  // inert, so a keyboard can't wander into the plan cards under the
  // overlay, and it hands focus back to the thumbnail on close. Popover
  // gives neither. What it does give for free is light dismiss, which is
  // the one line of JS below — a click landing on the dialog element
  // itself is a click on its backdrop, because the figure covers every
  // pixel inside it.
  const zoomDialog = root.querySelector('[data-zoom-dialog]');
  const zoomImg = root.querySelector('[data-zoom-img]');
  const zoomName = root.querySelector('[data-zoom-name]');
  const zoomPrice = root.querySelector('[data-zoom-price]');
  let zoomOpener = null;

  // Wraps a bundle thumbnail in its trigger. The plan card is itself a
  // <button>, so its rows get a role="button" span — a nested <button>
  // element there is invalid HTML. The cart list has no button ancestor
  // and gets a real one. Rows with no photo stay inert; there is nothing
  // to enlarge.
  function zoomThumbHtml(row, thumbHtml, insideButton) {
    if (!ENABLE_PHOTO_ZOOM) return thumbHtml;
    if (!row || !row.gid || !row.imageLarge) return thumbHtml;
    const inner = `${thumbHtml}<span class="sf__zoom-cue" aria-hidden="true">🔍</span><span class="sf__zoom-label">View a larger photo of ${esc(row.name)}</span>`;
    return insideButton
      ? `<span class="sf__zoom-trigger" role="button" tabindex="0" data-zoom-gid="${row.gid}">${inner}</span>`
      : `<button type="button" class="sf__zoom-trigger" data-zoom-gid="${row.gid}">${inner}</button>`;
  }

  function openZoom(trigger) {
    if (!zoomDialog || !zoomImg) return;
    const row = candidateDisplay.get(trigger.dataset.zoomGid);
    if (!row || !row.imageLarge) return;
    zoomOpener = trigger;
    zoomImg.src = row.imageLarge;
    zoomImg.alt = row.name;
    if (zoomName) zoomName.textContent = row.name;
    if (zoomPrice) zoomPrice.textContent = row.price ? `${formatMoney(row.price)} value — yours free` : 'Yours free';
    zoomDialog.classList.remove('is-closing');
    zoomDialog.showModal();
  }

  // Closing runs an animation first, so the dialog is held open until it
  // finishes. `settled` guards the two ways that can end: the animation
  // reporting done, and a timer for when it never runs at all — a
  // backgrounded tab, or a browser that skips it.
  function closeZoom() {
    if (!zoomDialog || !zoomDialog.open || zoomDialog.classList.contains('is-closing')) return;
    let settled = false;
    const onAnimationEnd = (event) => {
      if (event.target === zoomDialog) finish();
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      zoomDialog.removeEventListener('animationend', onAnimationEnd);
      zoomDialog.classList.remove('is-closing');
      zoomDialog.close();
    };
    zoomDialog.addEventListener('animationend', onAnimationEnd);
    setTimeout(finish, 400);
    zoomDialog.classList.add('is-closing');
  }

  if (zoomDialog) {
    zoomDialog.addEventListener('click', (event) => {
      if (event.target === zoomDialog) closeZoom();
    });
    const closeBtn = zoomDialog.querySelector('[data-zoom-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeZoom);
    // Escape closes a dialog outright, skipping the animation. Cancel the
    // default and route it through the same close as every other exit.
    zoomDialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeZoom();
    });
    zoomDialog.addEventListener('close', () => {
      zoomDialog.classList.remove('is-closing');
      // <dialog> restores focus to whatever was focused when it opened,
      // which after a tap on a span isn't reliably the thumbnail. Put it
      // back by hand when focus has fallen through to the page.
      if (zoomOpener && zoomOpener.isConnected && (!document.activeElement || document.activeElement === document.body)) {
        zoomOpener.focus();
      }
      zoomOpener = null;
      // Drop the photo so the next open can never flash the last box.
      zoomImg.removeAttribute('src');
      zoomImg.alt = '';
    });
  }

  // Both lists are rebuilt with innerHTML on every change, so the triggers
  // are read from the page rather than bound to. Capture, not bubble: the
  // plan panel's trigger sits inside the plan card's own button, and by
  // the time a bubbling click reached here the card would already have
  // re-picked its plan.
  root.addEventListener(
    'click',
    (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-zoom-gid]') : null;
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      openZoom(trigger);
    },
    true
  );

  root.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target instanceof Element ? event.target.closest('[data-zoom-gid]') : null;
      // A real <button> already fires a click from both keys.
      if (!trigger || trigger.tagName === 'BUTTON') return;
      event.preventDefault();
      event.stopPropagation();
      openZoom(trigger);
    },
    true
  );

  function allocationsFor(product, variantId) {
    const variant = product.variants.find((v) => v.id === Number(variantId));
    if (!variant) return {};
    const out = {};
    for (const term of TERMS) {
      const group = (product.selling_plan_groups || []).find((g) => term.match.test(g.name));
      if (!group) continue;
      const planIds = group.selling_plans.map((sp) => sp.id);
      const alloc = (variant.selling_plan_allocations || []).find((a) => planIds.includes(a.selling_plan_id));
      if (alloc) out[term.key] = { planId: alloc.selling_plan_id, price: alloc.price };
    }
    return out;
  }

  // ---------- Navigation ----------
  const backBtn = root.querySelector('[data-back-btn]');
  const continueBtn = root.querySelector('[data-continue-btn]');
  const continueLabel = root.querySelector('[data-continue-label]');
  const totalbarRow = root.querySelector('[data-totalbar-row]');

  // The sticky bar's height in a custom property, so the page's bottom padding
  // can clear it exactly. It changes with the badge row and with a wrapped
  // badge row, and a hardcoded padding left the last line of copy underneath.
  window.addEventListener('resize', () => fitFamilyBadges());
  const totalbarEl = root.querySelector('[data-totalbar]');
  if (totalbarEl && 'ResizeObserver' in window) {
    const setBarHeight = () => {
      root.style.setProperty('--sf-bar-h', `${Math.ceil(totalbarEl.getBoundingClientRect().height)}px`);
    };
    new ResizeObserver(setBarHeight).observe(totalbarEl);
    setBarHeight();
  }
  const progressSteps = root.querySelectorAll('[data-progress-step]');
  const progressCount = root.querySelector('[data-progress-count]');
  // The visible "Step 2 of 3" changes silently, so screen readers are told in
  // a hidden live region, with the step's name. Nothing on screen changes.
  const stepStatus = document.createElement('p');
  stepStatus.className = 'sf__sr-only';
  stepStatus.setAttribute('role', 'status');
  stepStatus.setAttribute('aria-live', 'polite');
  stepStatus.setAttribute('aria-atomic', 'true');
  if (progressCount) progressCount.after(stepStatus);
  let announcedStep = 1;
  // Unique ids for the plan cards' name parts; cards are re-rendered often.
  let planUid = 0;
  let maxStepReached = 1;
  const summaryEl = root.querySelector('[data-summary]');
  // Declared here rather than beside the roster code below because
  // updateSummary hides and shows it, and that runs during init.

  // On the final step the button toggles live with the summary's
  // visibility: "Continue" (scroll to the Wildlife Journey summary) while
  // it's out of view, "Checkout" once it's in view — reverting if the
  // shopper scrolls it back out. Same viewport idea as steps 1 & 3.
  let summaryInView = false;
  let canCheckout = false;
  let checkoutBusy = false;
  let checkoutShown = false;
  let plansRevealed = false; // step 2 scroll-to-plans fires once, then Continue always advances

  new IntersectionObserver((entries) => {
    summaryInView = entries[0].intersectionRatio >= 0.35;
    updateContinueUI();
  }, { threshold: [0, 0.35, 1], rootMargin: '0px 0px -90px 0px' }).observe(summaryEl);

  // On step 1, Continue first scrolls to the age tiers whenever they're
  // not currently in view — below the fold on a small screen, or scrolled
  // back out. Live check each tap (not a one-time "seen" flag), so the
  // shopper always sees the age choice before advancing.
  const tiersEl = root.querySelector('.sf__tiers');
  const planCardsEl = root.querySelector('[data-plan-cards]');
  const kidsEl = root.querySelector('.sf__sibling-cards');
  const flashboxGridEl = root.querySelector('[data-flashbox-grid]');
  // Options element to anchor Continue to, per step (1 = age, 2 = plan,
  // sibling step = kids, flash-sale step = past-box grid). config.siblingStep
  // and config.flashboxStep are 0 when their step is off, so they never
  // match a real step number.
  function stepOptions(n) {
    if (n === 1) return tiersEl;
    if (n === 2) return planCardsEl;
    if (n === config.siblingStep) return kidsEl;
    if (n === config.flashboxStep) return flashboxGridEl;
    return null;
  }
  function elInView(el) {
    if (!el) return true;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const usableBottom = vh - 110; // discount the fixed bottom bar
    const visible = Math.min(rect.bottom, usableBottom) - Math.max(rect.top, 0);
    return visible >= Math.min(rect.height, 200) * 0.4;
  }

  let stepTimer = null;
  const STEP_TRANSITION = ['is-leaving', 'from-right', 'from-left', 'to-left', 'to-right'];
  function unfreeze(el) { el.style.top = ''; }
  function goToStep(n, direction = 1, opts = {}) {
    // Capture the scroll position FIRST: taking the outgoing step out of
    // flow (below) collapses the page height and the browser clamps the
    // scroll to 0, so reading scrollY afterwards would give 0 and the
    // freeze would jump to the top.
    const scrollTop = window.scrollY;
    // Finish any transition still in flight so a fast second nav (e.g.
    // browser back/forward) starts from a clean slate.
    if (stepTimer) { clearTimeout(stepTimer); stepTimer = null; }
    root.querySelectorAll('.sf__step.is-leaving').forEach((s) => { s.classList.remove('is-active'); unfreeze(s); });
    root.querySelectorAll('.sf__step').forEach((s) => s.classList.remove(...STEP_TRANSITION));

    const current = root.querySelector('[data-step].is-active');
    const next = root.querySelector(`[data-step="${n}"]`);
    if (!next || current === next) return;

    // Slider: the outgoing step slides out while the incoming slides in.
    // Take the outgoing out of flow and shift it up by the scroll amount
    // (top: -scrollY) so it slides from wherever the shopper is scrolled —
    // no jump to the top — while staying clipped to the column by
    // .sf__steps. The incoming comes in fresh at the top.
    const forward = direction > 0;
    if (current) {
      current.classList.add('is-leaving');
      current.style.top = -scrollTop + 'px';
    }
    next.classList.add('is-active', forward ? 'from-right' : 'from-left');
    window.scrollTo({ top: 0, behavior: 'instant' }); // outgoing is frozen via top offset
    // Continue is the last control on the page, so focus left on it meant the
    // next Tab wrapped out of the page and back through the header before
    // reaching the new step. Focus the step itself instead: Tab then goes
    // straight into it, and a screen reader reads the step's label ("Step 2:
    // Pick a plan"), which stands in for the status announcement below. It is
    // a container, not a control, so it shows no focus ring.
    const focusMoved = !next.contains(document.activeElement);
    if (focusMoved) {
      next.tabIndex = -1;
      next.style.outline = 'none';
      next.focus({ preventScroll: true });
    }
    void next.offsetWidth; // lock in the off-screen start before animating
    requestAnimationFrame(() => {
      next.classList.remove('from-right', 'from-left');
      if (current) current.classList.add(forward ? 'to-left' : 'to-right');
    });
    stepTimer = setTimeout(() => {
      if (current) { current.classList.remove('is-active', 'is-leaving', 'to-left', 'to-right'); unfreeze(current); }
      stepTimer = null;
    }, 380);

    state.step = n;
    if (n === config.lastStep && vestEl && vestEl.classList.contains('is-open')) playVest('is-entering');
    // Published as a name, not a number: the numbering is computed at render
    // from which optional steps are on, so the same digit means different
    // things between configurations. Set here, beside the switch it
    // describes — this is state the flow already tracks, not analytics. The
    // A/B framework observes this attribute and owns any beacon.
    root.setAttribute('data-ab-step', analytics.stepName(n));
    analytics.step(n, n > maxStepReached);
    if (n > maxStepReached) maxStepReached = n;
    progressSteps.forEach((el) => {
      const num = Number(el.dataset.progressStep);
      el.classList.toggle('is-active', num === n);
      el.classList.toggle('is-done', num < n);
      // A step number is tappable once the shopper has reached it — never
      // ahead of where they've been.
      const jump = el.querySelector('[data-progress-jump]');
      if (jump) jump.disabled = num > maxStepReached;
    });
    backBtn.hidden = n === 1;
    if (progressCount) progressCount.textContent = `Step ${n} of ${config.lastStep}`;
    if (n !== announcedStep && !focusMoved) {
      const label = root.querySelector(`[data-progress-step="${n}"] .sf__progress-label`)?.textContent.trim();
      stepStatus.textContent = `Step ${n} of ${config.lastStep}${label ? `: ${label}` : ''}`;
    }
    announcedStep = n;
    if (opts.push !== false) history.pushState({ sfStep: n, sfNav: NAV_TOKEN }, '');
    updateContinueUI();
    updateSummary();
  }

  // Steps move through the browser history stack so the Back button (and
  // the progress numbers) rewind a step: forward moves push an entry,
  // backward moves pop one (popstate re-renders without re-pushing).
  // `history.go()` and `history.back()` traverse asynchronously: popstate
  // lands on a later task. A second navigation tapped before that arrives
  // computes its delta from a `state.step` the first one is about to
  // change, so the two traversals compound and the shopper lands somewhere
  // neither tap asked for — most visibly Back moving *forward*, and at the
  // bottom of the stack, off the page entirely. One traversal at a time.
  // Every entry this page instance pushes is stamped with a token minted on
  // load, and the entry we loaded onto is re-stamped to step 1.
  //
  // Without it, a history entry left by an EARLIER visit to this page is
  // indistinguishable from one we just pushed. Reloading — or coming back
  // from checkout — keeps the tab's stack and the `{sfStep: 3}` still sitting
  // on the entry being reloaded, so Back from step 2 popped a step 3 the
  // shopper had reached minutes ago and the flow jumped forward. Anything
  // unstamped is somebody else's entry and resolves to step 1.
  const NAV_TOKEN = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  history.replaceState({ sfStep: 1, sfNav: NAV_TOKEN }, '');

  let navBusy = false;
  let navTimer = null;
  function beginNav() {
    navBusy = true;
    clearTimeout(navTimer);
    // A traversal the browser refuses — already at the end of the stack —
    // fires no popstate, and must not wedge navigation for good.
    navTimer = setTimeout(() => { navBusy = false; }, 700);
  }
  function endNav() {
    clearTimeout(navTimer);
    navTimer = null;
    navBusy = false;
  }

  // Returns whether a navigation was started, so a caller that has
  // follow-up work (re-seeding the roster) can skip it when the tap was
  // swallowed by an in-flight traversal.
  function go(n) {
    if (navBusy || n === state.step || n < 1) return false;
    if (n < state.step) {
      beginNav();
      history.go(n - state.step);
    } else {
      goToStep(n, 1);
    }
    return true;
  }

  function stepComplete(n) {
    // At least one box in the roster. Counted from the roster rather than
    // priced explorers, so Continue does not wait on the price fetch.
    if (n === 1) return state.explorers.length > 0;
    if (n === 2) return !!state.term;
    return true;
  }

  function updateContinueUI() {
    if (checkoutBusy) return;
    let waiting = false;
    if (state.step < config.lastStep) {
      continueLabel.textContent = 'Continue ➜';
      // Keep the button tappable even when the step isn't complete so a
      // tap can point the shopper at what's missing (see nudgeStep).
      // A silently disabled button just reads as broken — especially on
      // mobile, where a mis-tap can leave no age selected.
      continueBtn.disabled = false;
      waiting = !stepComplete(state.step);
    } else if (!summaryInView) {
      continueLabel.textContent = 'Continue ➜';
      continueBtn.disabled = false;
      waiting = vestMissing();
    } else {
      continueLabel.textContent = config.checkoutLabel;
      continueBtn.disabled = !canCheckout;
      waiting = vestMissing();
    }
    continueBtn.classList.toggle('sf__btn--waiting', waiting);
    const isCheckout = state.step === config.lastStep && summaryInView;
    continueBtn.classList.toggle('sf__btn--go', isCheckout);
    // One-shot label bounce the moment it becomes Checkout (the button's
    // gentle pulse is a continuous CSS animation on .sf__btn--go).
    if (isCheckout && !checkoutShown) {
      continueBtn.classList.remove('is-celebrating');
      void continueBtn.offsetWidth; // restart the animation
      continueBtn.classList.add('is-celebrating');
    } else if (!isCheckout) {
      continueBtn.classList.remove('is-celebrating');
    }
    checkoutShown = isCheckout;
  }

  function nudgeStep(n) {
    const target = n === 1
      ? root.querySelector('.sf__tiers')
      : root.querySelector('[data-plan-cards]');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('is-prompting');
    setTimeout(() => target.classList.remove('is-prompting'), 1400);
  }

  backBtn.addEventListener('click', () => {
    if (navBusy || state.step <= 1) return;
    // Out of an emptied cart, Back is the same gesture as "Add a
    // subscription": straight to step 1 with the default tier restored.
    if (rosterOn() && state.explorers.length === 0) {
      restartFromEmpty();
      return;
    }
    beginNav();
    history.back();
  });
  continueBtn.addEventListener('click', () => {
    if (state.step < config.lastStep) {
      // If this step's options aren't in view (below the fold, or scrolled
      // back out), scroll to them first instead of advancing.
      const optionsEl = stepOptions(state.step);
      if (optionsEl) {
        if (state.step === 2) {
          // One-time: on a viewport too short to show every tier, the first
          // tap pulls the (tall) plan list up to the top so the lower tiers
          // are reachable. Once revealed, Continue always advances. Gated
          // on the 6-month card specifically (not the full list, which
          // also includes 3-month/monthly below it) — that's as far as a
          // shopper needs to have manually scrolled for Continue to just
          // advance instead of auto-scrolling further.
          const sixMoCard = optionsEl.querySelector('[data-term-key="6mo"]') || optionsEl;
          const rect = sixMoCard.getBoundingClientRect();
          const usableBottom = (window.innerHeight || document.documentElement.clientHeight) - 110;
          if (!plansRevealed && rect.bottom > usableBottom) {
            plansRevealed = true;
            optionsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
        } else if (!elInView(optionsEl)) {
          optionsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      if (stepComplete(state.step)) go(state.step + 1);
      else nudgeStep(state.step);
      return;
    }
    // The vest size comes first: Continue (cart below the fold) and
    // Checkout (cart in view) both go back up to it until one is picked.
    if (vestMissing()) {
      promptVest();
      return;
    }
    if (!summaryInView) {
      summaryEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    runCheckout();
  });

  // Browser Back/Forward → sync the visible step to the popped entry.
  window.addEventListener('popstate', (e) => {
    endNav();
    // An entry from an earlier visit carries a step we never pushed; treat
    // it as the start of the flow rather than obeying it.
    const owned = e.state && e.state.sfNav === NAV_TOKEN;
    const target = (owned && e.state.sfStep) || 1;
    goToStep(target, target < state.step ? -1 : 1, { push: false });
  });

  // Returning from Shopify checkout restores this page from the back/forward
  // cache with checkout still mid-flight — the button frozen on "Packing
  // your box…". Reset it (and clear any stuck confetti) whenever the page
  // is shown again so the shopper lands back on a live Checkout button.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.sf__confetti-canvas').forEach((c) => c.remove());
    if (!checkoutBusy) return;
    checkoutBusy = false;
    continueBtn.disabled = false;
    updateContinueUI();
  });

  // Progress numbers jump to any step already reached.
  progressSteps.forEach((el) => {
    const jump = el.querySelector('[data-progress-jump]');
    if (!jump) return;
    jump.addEventListener('click', () => {
      const target = Number(jump.dataset.progressJump);
      if (target <= maxStepReached && target !== state.step) go(target);
    });
  });

  // ---------- Step 1: age (labeled contents image above the options) ----------
  // Slide position is shared across tiers so shoppers can flip between
  // ages and land on the same photo for a quick side-by-side compare.
  let sharedGallerySlide = 0;

  function updateGalleryControls(gallery, index) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return;
    // Prefer the event's index: on a drag the slideshow dispatches
    // slideshow:select *before* updating `current`, so reading `current`
    // here would lag one slide (the arrow wouldn't reappear).
    // Total comes from the server-stamped data-slide-count so it's right on
    // first paint: reading slideshow.slides at init runs before the custom
    // element hydrates (refs.slides is still empty), returning 0 — that made
    // `current >= total - 1` true and wrongly hid the next arrow until the
    // first swipe. The stamped count also excludes the mid-transition
    // placeholder slide that a raw DOM count would include.
    const total = Number(gallery.dataset.slideCount)
      || (slideshow.slides ? slideshow.slides.length : 0);
    const current = typeof index === 'number' ? index : (slideshow.current || 0);
    const prevBtn = gallery.querySelector('.sf__carousel-arrow--prev');
    const nextBtn = gallery.querySelector('.sf__carousel-arrow--next');
    if (prevBtn) prevBtn.classList.toggle('sf__carousel-arrow--boundary', current <= 0);
    if (nextBtn) nextBtn.classList.toggle('sf__carousel-arrow--boundary', current >= total - 1);
    gallery.querySelectorAll('.sf__carousel-dot').forEach((dot, i) => {
      const active = i === current;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  root.querySelectorAll('[data-tier-gallery]').forEach((gallery) => {
    updateGalleryControls(gallery);
    gallery.addEventListener('slideshow:select', (e) => {
      sharedGallerySlide = e.detail.index;
      updateGalleryControls(gallery, e.detail.index);
    });
    gallery.querySelectorAll('.sf__carousel-dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        const slideshow = gallery.querySelector('slideshow-component');
        if (slideshow && typeof slideshow.select === 'function') {
          slideshow.select(Number(dot.dataset.dotIndex));
        }
      });
    });
  });

  // Shows the gallery belonging to a tier index and leaves the rest hidden.
  // Driven by the first explorer in the roster, so removing the first box
  // moves the pictures to whoever is now first.
  function showTierGallery(tierIndex) {
    // An empty roster has no first explorer, which would otherwise hide
    // every gallery and leave step 1 with a hole where the artwork is. The
    // default tier's pictures stand in until a box is chosen again.
    if (tierIndex == null && defaultTierEl) tierIndex = defaultTierEl.dataset.tierIndex;
    root.querySelectorAll('[data-tier-gallery]').forEach((gallery) => {
      gallery.hidden = gallery.dataset.tierGallery !== String(tierIndex);
      if (gallery.hidden) return;
      const slideshow = gallery.querySelector('slideshow-component');
      // A tier on the 4-slide fallback holds fewer slides than its neighbours,
      // so the shared index is clamped to this gallery's own length.
      const last = Math.max(0, (Number(gallery.dataset.slideCount) || 1) - 1);
      const target = Math.min(sharedGallerySlide, last);
      if (slideshow && typeof slideshow.select === 'function' && slideshow.current !== target) {
        slideshow.select(target, null, { animate: false });
      }
      updateGalleryControls(gallery);
    });
  }

  // Default: ages 6-8 tier, unless ?tier= names another one. The homepage
  // "Which box should I get?" cards link here with the tier the visitor
  // already picked (?tier=zookeeper), so it must not be thrown away.
  // Matches a variant id exactly, or any part of a variant title, so
  // ?tier=zookeeper and ?tier=3-5 both land on "Zookeeper (Age 3 - 5)".
  //
  // Resolved here but applied at the end of the roster block below: seeding
  // calls `pickTier`, which closes over consts that block declares, and
  // running it before those initialise is a temporal-dead-zone throw.
  const tierWrappers = [...root.querySelectorAll('[data-tier]')];
  const tierKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const requestedTier = tierKey(new URLSearchParams(window.location.search).get('tier'));
  const requestedTierEl = requestedTier
    ? tierWrappers.find(
        (t) =>
          t.dataset.variantId === requestedTier ||
          tierKey(t.dataset.variantTitle).includes(requestedTier)
      )
    : null;
  const defaultTierEl =
    requestedTierEl || tierWrappers.find((t) => /6\s*-\s*8/.test(t.dataset.variantTitle)) || tierWrappers[1];

  // ---------- Step 2: plans (vertical list, 12-month preselected) ----------
  // The free-gift panel on the selected card. Bucket A pictures the one gift
  // product; bucket B pictures the tier's past-box bundle, one row per box,
  // with the boxes' own prices struck through and summed.
  // The gift is earned per box, so the panel counts them. The collapsed
  // label on the card already said "3 FREE Gifts"; this is the same fact
  // stated where the shopper reads the value, and it has to agree with the
  // receipt, which multiplies both the quantity and the struck-out price.
  function giftPanelHtml(count = 1) {
    const n = Math.max(1, count);
    const qtyLabel = n > 1 ? ` × ${n}` : '';
    return `
          <span class="sf__plan-gift">
            ${config.giftImage ? `<img src="${config.giftImage}" alt="" width="64" height="64" loading="lazy">` : '🎁'}
            <span><strong>🎁 FREE ${esc(config.giftTitle)}${qtyLabel}</strong><em><b>${formatMoney(config.giftPrice * n)} value</b> — ${n > 1 ? 'one in each first box' : 'included in your first box'} automatically</em></span>
          </span>`;
  }

  // `boxes` is the roster size: the panel pictures one child's bundle, so a
  // multi-box order gets a multiplier beside FREE rather than a second set of
  // thumbnails saying the same thing again.
  function bundlePanelHtml(rows, boxes = 1, withVest = false) {
    const vestPrice = withVest && VEST ? VEST.price || 0 : 0;
    const value = bundleValueOf(rows) + vestPrice;
    const title = bundleCopy(config.bundleTitle, config.bundleTitleOne, rows.length);
    const sub = bundleCopy(config.bundleSub, config.bundleSubOne, rows.length);
    return `
          <span class="sf__plan-bundle">
            <span class="sf__plan-bundle-head">
              <strong class="sf__plan-bundle-title">${esc(title || 'Free past boxes')}</strong>
              ${sub ? `<em class="sf__plan-bundle-sub">${escBold(sub)}</em>` : ''}
            </span>
            <span class="sf__plan-bundle-list">${rows
              .map(
                (row, index) => `
              <span class="sf__plan-bundle-item" style="--sf-bundle-row: ${index}">
                ${row.image
                  ? zoomThumbHtml(row, `<img class="sf__plan-bundle-thumb" src="${row.image}" alt="" width="52" height="52" loading="lazy">`, true)
                  : '<span class="sf__plan-bundle-thumb sf__plan-bundle-thumb--ph" aria-hidden="true">🎁</span>'}
                <span class="sf__plan-bundle-name">${escPastBoxName(row.name)}</span>
                ${row.price ? `<s class="sf__plan-bundle-price">${formatMoney(row.price)}</s>` : ''}
              </span>`
              )
              .join('')}${withVest && VEST
              ? `
              <span class="sf__plan-bundle-item sf__plan-bundle-item--vest" style="--sf-bundle-row: ${rows.length}">
                ${VEST.image ? `<img class="sf__plan-bundle-thumb" src="${VEST.image}" alt="" width="52" height="52" loading="lazy">` : '<span class="sf__plan-bundle-thumb sf__plan-bundle-thumb--ph" aria-hidden="true">🎁</span>'}
                <span class="sf__plan-bundle-name">${esc(VEST.title || VEST.name)}<small>For included patches. Pick a size at the next step.</small></span>
                ${vestPrice ? `<s class="sf__plan-bundle-price">${formatMoney(vestPrice)}</s>` : ''}
              </span>`
              : ''}</span>
            ${value > 0
              ? `<span class="sf__plan-bundle-total"><b>${formatMoney(value)} value</b> — <span class="sf__plan-bundle-free">FREE</span>${boxes > 1 ? `<b class="sf__plan-bundle-times">\u00D7 ${boxes}</b>` : ''}</span>`
              : ''}
          </span>`;
  }

  // The chosen-explorer pill at the top of the plan step. Its own function
  // because the name can be typed after the cards were built, and
  // rebuilding every card on each keystroke to refresh one pill is work
  // nobody asked for.
  // Reiterates what step 1 chose, at the top of the plan step. One box keeps
  // the single pill it has always been; several split into a count and a
  // badge per tier, because "2 boxes" and "which two" are different facts
  // and running them together in one sentence made neither scannable.
  function refreshChosenPill() {
    const chosen = root.querySelector('[data-chosen-tier]');
    if (!chosen) return;
    const roster = readyExplorers();
    const multi = roster.length > 1;
    chosen.classList.toggle('sf__chosen--multi', multi);

    if (!roster.length) {
      chosen.innerHTML = '';
      chosen.hidden = true;
      return;
    }

    const shortFor = (variantId) => {
      const el = tierEls.find((t) => t.dataset.variantId === String(variantId));
      return el && el.dataset.variantShort ? el.dataset.variantShort : '';
    };

    if (!multi) {
      chosen.innerHTML =
        `<span class="sf__chosen-check" aria-hidden="true">✓</span>` +
        esc(roster[0].title);
      chosen.hidden = false;
      return;
    }

    // Grouped per tier, matching the step-1 stepper: two children of the same
    // age are one card there and one badge here, counted rather than repeated.
    const byTier = new Map();
    roster.forEach((x) => {
      const key = String(x.variantId);
      byTier.set(key, (byTier.get(key) || 0) + 1);
    });
    const badges = [...byTier].map(([variantId, n]) => {
      const name = shortFor(variantId) || tierTitleFor(variantId);
      return `<span class="sf__chosen-badge">` +
        `<span class="sf__chosen-check" aria-hidden="true">✓</span>` +
        `${esc(name)}${n > 1 ? `<span class="sf__chosen-mult"> × ${n}</span>` : ''}</span>`;
    }).join('');

    chosen.innerHTML =
      `<span class="sf__chosen-count"><span class="sf__chosen-countemoji" aria-hidden="true">📦</span>${roster.length} boxes</span>` +
      `<span class="sf__chosen-badges">${badges}</span>`;
    chosen.hidden = false;
  }

  function buildPlanCards() {
    const wrap = root.querySelector('[data-plan-cards]');
    const allocs = allocationsFor(boxData, state.ageVariantId);
    const monthly = allocs.monthly;
    // With more than one box the card prices the whole family: a per-box
    // figure stops meaning anything the moment two tiers cost different
    // amounts, and the total is the number the parent is deciding on. The
    // per-box breakdown is on the receipt, which itemises anyway.
    const roster = readyExplorers();
    const boxes = Math.max(1, roster.length);
    const familyPricesFor = (termKey) =>
      roster.map((x) => {
        const a = allocationsFor(boxData, x.variantId)[termKey];
        return a ? a.price : 0;
      });
    const familyPriceFor = (termKey) => familyPricesFor(termKey).reduce((sum, p) => sum + p, 0);
    const familyMonthly = familyPriceFor('monthly');
    // Second chip beside the plan discount: the family rate Recharge adds.
    const familyPct = familyPercentFor(totalBoxes());
    // Parsed once per build: every term below asks the same offer the same
    // questions.
    const app = bxgyConfig();
    const offer = effectiveOffer(app);
    const giftOn = giftOffered(app);
    const bundleOn = bundleMode(app);
    const bundleRows = bundleOn ? tierBundleRows(offer) : [];
    wrap.innerHTML = '';

    // Reiterate the chosen age tier at the top of the plan step.
    refreshChosenPill();

    TERMS.forEach((term) => {
      const alloc = allocs[term.key];
      if (!alloc) return;
      const perBox = Math.round(alloc.price / term.months);
      // One box quotes its tier; several quote the sum across the roster.
      const shownTotal = boxes > 1 ? familyPriceFor(term.key) : alloc.price;
      const monthlyRef = boxes > 1 ? familyMonthly : (monthly ? monthly.price : 0);
      // Every discount the household actually gets, applied in the order the
      // charge applies them: the plan's prepaid price first, then the multibox
      // rate on top, taken off each line and truncated to the cent exactly as
      // updateSummary does it — anything else puts the card off the receipt.
      const netTotal = rosterOn() && familyPct > 0
        ? shownTotal - familyOffFor(boxes > 1 ? familyPricesFor(term.key) : [alloc.price], familyPct)
        : shownTotal;
      // Per box, exactly as a single-box card quotes it. A monthly figure read
      // as a monthly charge on a plan that bills the whole term up front, and
      // "per box" makes no claim about when the card is charged. Across two
      // tiers it is an average — they genuinely cost different amounts — and the
      // total bar carries the figure that is actually charged.
      const perBoxNet = Math.round(netTotal / (term.months * Math.max(1, boxes)));
      const perBoxRef = monthlyRef ? Math.round(monthlyRef / Math.max(1, boxes)) : 0;
      const saveTotal = monthlyRef && term.months > 1 ? monthlyRef * term.months - netTotal : 0;
      // Floor, not round: 30% off = 3.6 months, so "3 months free" is
      // accurate — rounding up to 4 would overstate the savings.
      const freeMonths = term.months === 12 && monthlyRef ? Math.floor(saveTotal / monthlyRef) : 0;
      // Per-box discount vs the monthly price (12/6/3-month → 30/20/15%).
      // Still a per-box figure, and still correct across a mixed roster:
      // every tier carries the same percentage for a given term.
      const offPct = monthly && term.months > 1 ? Math.round((1 - perBox / monthly.price) * 100) : 0;
      // Bucket B only advertises a bundle it can actually picture — if every
      // candidate for this tier is sold out, the card just carries no gift.
      const hasGift = giftOn && planQualifies(alloc.planId, offer) && (!bundleOn || bundleRows.length > 0);
      const withVest = !!VEST && bundleOn && hasGift && term.key === '12mo';
      const selected = state.term === term.key;
      // What the money buys, under the price: how many boxes arrive each month
      // and for how long. A single box keeps `/ box` up on the price line — the
      // figure is per box, and dropping it would read as a total — so its line
      // carries only the term. The monthly plan has no term to name.
      const months = term.months > 1 ? `for ${term.months} months` : '';
      const termLine = boxes > 1
        ? `${boxes} boxes per month${months ? ` ${months}` : ''}`
        : months;
      // What this plan earns, in the order the shopper meets it: the per-box
      // discount, the multibox rate, then the gift. Separated by `+` here — the
      // plan cards are where "these stack" gets said; the total bar below
      // repeats the same chips without separators.
      // Two groups, not five loose chips: the row wraps between them and never
      // inside one, so a narrow card breaks as
      //   30% off each box + 5% multibox discount
      //   + FREE Gift x 2
      // rather than orphaning a `+` at the end of the first line.
      const discountChips = [
        offPct > 0 ? `<span class="sf__chip sf__chip--discount">${offPct}% off each box</span>` : '',
        rosterOn() && familyPct > 0 ? `<span class="sf__chip sf__chip--discount sf__chip--family">${familyPct}% multibox discount</span>` : '',
      ].filter(Boolean);
      // The `+` before the gift lives INSIDE its group, so it wraps down with
      // the chip it introduces rather than being left stranded at the end of
      // the line above.
      const giftGroup = rosterOn() && hasGift
        ? `<span class="sf__chip-group">${discountChips.length ? '<span class="sf__chip-plus" aria-hidden="true">+</span>' : ''}${giftChipHtml(boxes)}</span>`
        : '';
      const cardChips = [
        discountChips.length ? `<span class="sf__chip-group">${joinChips(discountChips)}</span>` : '',
        giftGroup,
      ].filter(Boolean);

      // The was-price sits immediately after the amount it strikes out, and
      // before `/ box` — the unit belongs to the price the shopper pays, not to
      // the one they aren't paying.
      // Read aloud, the two prices were just two numbers. Hidden words make it
      // "$36.39 per box, was $51.99"; the visible "/ box" is left out of the
      // name so it isn't read twice.
      // A radio's accessible name is all of its text by default, which on a
      // card with a gift list ran to ~390 characters. Name it by plan and
      // price; the badge, saving and gift line follow as its description.
      const uid = `sf-plan-${++planUid}`;
      const priceHtml = boxes > 1
        ? `<span class="sf__plan-price" id="${uid}-price"><strong>${formatMoney(perBoxNet)}<span class="sf__sr-only"> per box</span></strong>${perBoxRef > perBoxNet ? `<s><span class="sf__sr-only">, was </span>${formatMoney(perBoxRef)}</s>` : ''}<span class="sf__plan-per" aria-hidden="true">/ box</span></span>`
        : `<span class="sf__plan-price" id="${uid}-price"><strong>${formatMoney(perBox)}<span class="sf__sr-only"> per box</span></strong>${term.months > 1 && monthlyRef ? `<s><span class="sf__sr-only">, was </span>${formatMoney(monthly.price)}</s>` : ''}<span class="sf__plan-per" aria-hidden="true">/ box</span></span>`;

      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'sf__plan' + (selected ? ' is-selected' : '');
      row.dataset.termKey = term.key;
      row.setAttribute('role', 'radio');
      row.setAttribute('aria-checked', String(selected));
      row.innerHTML = `
        ${term.badge ? `<span class="sf__plan-badge" id="${uid}-badge">${term.badge}</span>` : ''}
        <span class="sf__plan-main">
          <span class="sf__plan-name" id="${uid}-name">${term.name}<span class="sf__plan-sub">${term.sub}</span></span>
          ${rosterOn() ? '' : priceHtml}
          ${!rosterOn() && offPct > 0 ? `<span class="sf__chip sf__chip--discount">${offPct}% off each box</span>` : ''}
          ${SHOW_BILLED_TODAY ? `<span class="sf__plan-billed">${term.months > 1 ? `${formatMoney(netTotal)} billed today` : 'Billed every month'}</span>` : ''}
          ${hasGift
            ? bundleOn
              ? `<span class="sf__plan-giftmini" id="${uid}-gift">🎁 ${bundleRows.length * boxes} FREE Past ${bundleRows.length * boxes === 1 ? 'Box' : 'Boxes'}${withVest ? ' + Vest' : ''} Included!</span>`
              : `<span class="sf__plan-giftmini" id="${uid}-gift">🎁 ${boxes > 1 ? `${boxes} FREE Gifts` : 'FREE Gift'} Included!</span>`
            : ''}
        </span>
        <span class="sf__plan-side">
          ${saveTotal > 0 ? `<span class="sf__plan-save" id="${uid}-save">Save ${formatMoney(saveTotal)}${freeMonths >= 1 ? `<span class="sf__plan-save-sub">${freeMonths} months FREE</span>` : ''}</span>` : ''}
        </span>
        ${rosterOn() ? priceHtml : ''}
        ${rosterOn() && termLine ? `<span class="sf__plan-term">${termLine}</span>` : ''}
        ${rosterOn() && cardChips.length ? `<span class="sf__plan-chips">${cardChips.join('')}</span>` : ''}
        ${hasGift ? (bundleOn ? bundlePanelHtml(bundleRows, boxes, withVest) : giftPanelHtml(boxes)) : ''}
        <span class="sf__check" aria-hidden="true">✓</span>
      `;
      row.setAttribute('aria-labelledby', `${uid}-name ${uid}-price`);
      const described = ['badge', 'save', 'gift'].map((k) => `${uid}-${k}`).filter((id) => row.querySelector(`#${id}`));
      if (described.length) row.setAttribute('aria-describedby', described.join(' '));
      row.addEventListener('click', () => {
        state.term = term.key;
        wrap.querySelectorAll('.sf__plan').forEach((c) => {
          c.classList.toggle('is-selected', c === row);
          c.setAttribute('aria-checked', String(c === row));
        });
        updateContinueUI();
        updateSummary();
      });
      wrap.appendChild(row);
    });
    updateSummary();
  }

  // ---------- Step 3: how many kids ----------
  root.querySelectorAll('[data-kids-option]').forEach((card) => {
    card.addEventListener('click', () => {
      root.querySelectorAll('[data-kids-option]').forEach((c) => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');
      state.kids = Number(card.dataset.kids);
      updateSummary();
    });
  });

  // ---------- Step 4: add-ons ----------
  // Same grammar as the age cards: the card body adds the first one and
  // removes it again while there is only one, and from two upward the
  // stepper owns every change — so a stray tap can't wipe a quantity the
  // shopper built up. Every read of the DOM is `paintAddons` rendering
  // `state.addons` from scratch.
  //
  // A sized card (size chips inside it) is one card for several variants.
  // Its `data-variant-id` is the size currently picked, and every size is its
  // own entry in `state.addons`, so Small and Medium can both be in the order
  // as separate lines. Everything the order needs about a variant — card,
  // line title, price, picture — is read from `addonMeta`, never from the
  // card's live attributes, because the receipt can change a size the card
  // isn't showing.
  const addonEls = [...root.querySelectorAll('[data-addon]')];
  const addonStatusEl = root.querySelector('[data-addon-status]');
  const reduceMotion = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  // How long the green tick holds before the roster arm's stepper takes its
  // slot. Long enough to read as a confirmation, short enough not to wait on.
  const ADDON_CONFIRM_MS = 650;

  const addonMeta = new Map();
  addonEls.forEach((el) => {
    const sizes = [...el.querySelectorAll('[data-addon-size]')];
    if (!sizes.length) {
      const img = el.querySelector('.sf__addon-img');
      const compareRaw = el.dataset.addonCompare;
      addonMeta.set(String(el.dataset.variantId), {
        el,
        title: el.dataset.addonTitle,
        price: Number(el.dataset.addonPrice),
        // Flash-sale cards carry data-addon-compare (the original price);
        // regular add-ons don't, which is what flags them in the summary.
        compareAt: compareRaw ? Number(compareRaw) : null,
        image: img ? img.src : null,
      });
      return;
    }
    const base = el.dataset.addonBase || '';
    sizes.forEach((input) => {
      addonMeta.set(String(input.dataset.variantId), {
        el,
        input,
        label: input.dataset.label,
        title: `${base} — ${input.dataset.label}`,
        price: Number(input.dataset.price),
        priceLabel: input.dataset.priceLabel,
        compareAt: null,
        image: input.dataset.image || null,
      });
    });
  });

  function addonQty(id) {
    const entry = state.addons.get(String(id));
    return entry ? entry.qty : 0;
  }

  function announceAddon(message) {
    if (!addonStatusEl) return;
    addonStatusEl.textContent = '';
    requestAnimationFrame(() => { addonStatusEl.textContent = message; });
  }

  // Fades the card's picture, price and pill in fresh (CSS `is-switching`),
  // with the pill's colour transitions off so nothing plays as an add or a
  // removal. Restarted rather than stacked, so a quick second switch replays
  // it from the start.
  function fadeAddonFresh(el) {
    if (reduceMotion()) return;
    el.classList.remove('is-switching');
    void el.offsetWidth;
    el.classList.add('is-switching');
    clearTimeout(el._sfFade);
    el._sfFade = setTimeout(() => el.classList.remove('is-switching'), 320);
  }

  // Only ever called on a size going from not-in-the-order to in it, never
  // from a repaint, so the pop can't replay on unrelated changes. Removing
  // and reflowing first lets a quick re-add replay it from the start; the
  // class comes off when it finishes.
  function popAddon(el) {
    if (reduceMotion()) return;
    el.classList.remove('is-popping');
    void el.offsetWidth;
    el.classList.add('is-popping');
  }
  addonEls.forEach((el) => {
    el.addEventListener('animationend', (event) => {
      if (event.target === el && event.animationName === 'sf-addon-pop') el.classList.remove('is-popping');
    });
  });

  function paintAddons() {
    addonEls.forEach((el) => {
      const activeId = el.dataset.variantId;
      const n = addonQty(activeId);
      const meta = addonMeta.get(String(activeId));
      const options = [...el.querySelectorAll('[data-addon-option]')];
      el.classList.toggle('is-selected', n > 0);
      options.forEach((option) => {
        option.setAttribute('aria-pressed', String(n > 0));
        // Nothing to add an extra to: every add-on rides a subscription.
        option.disabled = rosterOn() && state.explorers.length === 0;
      });
      // Each size shows whether it is already in the order, so the card can
      // be looking at Medium while Small sits in the cart.
      el.querySelectorAll('[data-addon-size]').forEach((input) => {
        const inOrder = addonQty(input.dataset.variantId) > 0;
        input.closest('.sf__addon-size-chip').classList.toggle('is-in-order', inOrder);
        const name = input.parentElement.querySelector('[data-addon-size-name]');
        if (name) {
          const sold = input.disabled ? ', sold out' : '';
          name.textContent = `${input.dataset.label}, ${input.dataset.priceLabel}${sold}${inOrder ? ', added' : ''}`;
        }
      });

      const qty = el.querySelector('[data-addon-qty]');
      // Quantities on extras ride with the challenger rather than shipping
      // to both arms. They are an independent improvement, but giving one
      // arm a second way to raise its own order value would show up in the
      // test as the roster earning money the roster did not earn. While the
      // tick is playing the pill keeps the slot; the stepper then crossfades
      // in over it (CSS `is-stepping`), in the same box.
      const stepping = Boolean(qty) && rosterOn() && n > 0 && !el.classList.contains('is-confirming');
      el.classList.toggle('is-stepping', stepping);
      if (!qty) return;
      qty.hidden = false;
      const count = qty.querySelector('[data-addon-count]');
      if (count) count.textContent = String(n);
      // Don't strand keyboard focus on a control that just faded out.
      if (!stepping && qty.contains(document.activeElement)) {
        const back = el.querySelector('.sf__addon-pill-btn');
        if (back) back.focus();
      }
    });
  }

  // Roster arm only: hold the green tick for a beat, then hand the slot to
  // the stepper. Focus follows if the shopper was on the pill.
  function confirmAddon(el) {
    if (!rosterOn()) return;
    clearTimeout(el._sfConfirm);
    el.classList.add('is-confirming');
    el._sfConfirm = setTimeout(() => {
      const pill = el.querySelector('.sf__addon-pill-btn');
      const pillHadFocus = pill && document.activeElement === pill;
      el.classList.remove('is-confirming');
      paintAddons();
      if (pillHadFocus && el.classList.contains('is-stepping')) {
        const plus = el.querySelector('[data-addon-plus]');
        if (plus) plus.focus();
      }
    }, ADDON_CONFIRM_MS);
  }

  // The one writer. `delta` of 0 means "the card body was tapped".
  function setAddonQty(id, delta) {
    const key = String(id);
    const meta = addonMeta.get(key);
    if (!meta) return;
    const el = meta.el;
    const current = addonQty(key);

    let next;
    if (delta === 0) next = current === 0 ? 1 : current === 1 ? 0 : current;
    else next = Math.max(0, current + delta);
    // Control keeps prod's tap-to-add / tap-to-remove and nothing beyond it.
    if (!rosterOn()) next = Math.min(1, next);
    if (next === current) return;

    if (next === 0) {
      state.addons.delete(key);
    } else {
      const existing = state.addons.get(key);
      if (existing) {
        existing.qty = next;
      } else {
        state.addons.set(key, {
          title: meta.title,
          price: meta.price,
          compareAt: meta.compareAt,
          image: meta.image,
          qty: next,
        });
      }
    }

    const showing = el.dataset.variantId === key;
    if (current === 0 && showing) {
      popAddon(el);
      confirmAddon(el);
    }
    if (next === 0) {
      clearTimeout(el._sfConfirm);
      el.classList.remove('is-confirming');
    }
    if (current === 0) announceAddon(`${meta.title} added.`);
    else if (next === 0) announceAddon(`${meta.title} removed.`);

    paintAddons();
    updateSummary();
  }

  // Picking a size points the card at that variant: price, picture, labels
  // and whether it shows as added all follow. It never adds anything itself.
  function showAddonSize(el, input) {
    const meta = addonMeta.get(String(input.dataset.variantId));
    if (!meta || el.dataset.variantId === input.dataset.variantId) return;
    clearTimeout(el._sfConfirm);
    el.classList.remove('is-confirming');
    el.dataset.variantId = input.dataset.variantId;
    el.dataset.addonTitle = meta.title;
    el.dataset.addonPrice = String(meta.price);
    const priceEl = el.querySelector('[data-addon-price-text]');
    if (priceEl) priceEl.textContent = meta.priceLabel;
    const img = el.querySelector('.sf__addon-img');
    if (img && meta.image) img.src = meta.image;
    const pill = el.querySelector('.sf__addon-pill-btn');
    if (pill) pill.setAttribute('aria-label', `Add ${meta.title}, ${meta.priceLabel}`);
    el.querySelectorAll('[data-addon-minus]').forEach((b) => b.setAttribute('aria-label', `Remove one ${meta.title}`));
    el.querySelectorAll('[data-addon-plus]').forEach((b) => b.setAttribute('aria-label', `Add another ${meta.title}`));
    fadeAddonFresh(el);
    paintAddons();
  }

  // Ids are read at click time, not captured here: on a sized card the
  // variant behind the same buttons changes with the picked size.
  addonEls.forEach((el) => {
    el.querySelectorAll('[data-addon-option]').forEach((option) => {
      option.addEventListener('click', () => setAddonQty(el.dataset.variantId, 0));
    });
    const plus = el.querySelector('[data-addon-plus]');
    if (plus) plus.addEventListener('click', () => setAddonQty(el.dataset.variantId, 1));
    const minus = el.querySelector('[data-addon-minus]');
    if (minus) minus.addEventListener('click', () => setAddonQty(el.dataset.variantId, -1));
    el.querySelectorAll('[data-addon-size]').forEach((input) => {
      input.addEventListener('change', () => { if (input.checked) showAddonSize(el, input); });
    });
  });

  // ---------- Sold-out size tooltip ----------
  // On add-on cards and in the vest picker. Hover shows it on desktop through
  // CSS; a tap shows it on touch, for a few seconds or until the next tap
  // anywhere. Either way it is first nudged to stay inside the screen, or
  // inside the vest box, which clips. Delegated, because the vest rows are
  // cloned per explorer after load.
  const SOLD_OUT_SIZE = '.sf__addon-size-chip.is-sold-out, .sf__vest-size.is-sold-out';
  const TIP_MS = 2600;
  const TIP_EDGE = 8;
  let tippingChip = null;
  let tipTimer = 0;
  function fitTip(chip) {
    const tip = chip.querySelector('.sf__size-tip');
    if (!tip) return;
    const clip = chip.closest('.sf__vest-panel');
    const bounds = clip ? clip.getBoundingClientRect() : { left: 0, right: document.documentElement.clientWidth };
    // Measured from the size and the bubble's own width, not the bubble's
    // box, which is still scaled down for its pop-in while hidden.
    const chipRect = chip.getBoundingClientRect();
    const half = tip.offsetWidth / 2;
    const center = chipRect.left + chipRect.width / 2;
    const min = bounds.left + TIP_EDGE;
    const max = bounds.right - TIP_EDGE;
    let shift = 0;
    if (center - half < min) shift = min - (center - half);
    else if (center + half > max) shift = max - (center + half);
    tip.style.setProperty('--tip-shift', `${Math.round(shift)}px`);
  }
  function hideTip() {
    clearTimeout(tipTimer);
    if (tippingChip) tippingChip.classList.remove('is-tipping');
    tippingChip = null;
  }
  root.addEventListener('pointerover', (event) => {
    const chip = event.target.closest(SOLD_OUT_SIZE);
    if (chip && !chip.contains(event.relatedTarget)) fitTip(chip);
  });
  root.addEventListener('click', (event) => {
    const chip = event.target.closest(SOLD_OUT_SIZE);
    if (!chip) return;
    event.preventDefault();
    const again = tippingChip === chip;
    hideTip();
    if (again) return;
    fitTip(chip);
    chip.classList.add('is-tipping');
    tippingChip = chip;
    tipTimer = setTimeout(hideTip, TIP_MS);
  });
  document.addEventListener('pointerdown', (event) => {
    if (tippingChip && !tippingChip.contains(event.target)) hideTip();
  });

  // ---------- Summary ----------
  function currentBoxAlloc() {
    if (!boxData || !state.ageVariantId || !state.term) return null;
    return allocationsFor(boxData, state.ageVariantId)[state.term] || null;
  }
  // Same lookup for any explorer. One plan covers the family, so only the
  // tier changes between them — and each tier is priced differently, which
  // is why the total is summed per explorer rather than multiplied.
  function allocFor(x) {
    if (!boxData || !x || !x.variantId || !state.term) return null;
    return allocationsFor(boxData, x.variantId)[state.term] || null;
  }
  // A function declaration, not an arrow const: updateSummary runs during
  // init (from the default tier selection) before this point in the file.
  function readyExplorers() {
    return state.explorers.filter((x) => x.variantId && allocFor(x));
  }
  function updateSummary() {
    paintVest();
    const linesEl = root.querySelector('[data-summary-lines]');
    const totalEl = root.querySelector('[data-summary-total]');
    const fineEl = root.querySelector('[data-summary-fine]');
    const emptyEl = root.querySelector('[data-summary-empty]');
    const term = TERMS.find((t) => t.key === state.term);
    const boxAlloc = currentBoxAlloc();
    const siblingQty = state.kids - 1;
    let total = 0;
    // What the boxes would cost month to month with no plan and no family
    // discount — the struck-through figure beside the total.
    let fullPrice = 0;
    let boxTotal = 0;
    // Each box is its own cart line at quantity 1, and Recharge discounts each
    // one separately — so the multibox discount is summed per line, not taken
    // off boxTotal. See familyOffFor.
    const boxPrices = [];
    const lines = [];

    // One row per TIER, not per explorer: step 1 groups two same-age
    // children onto one card as "2 children" and the plan step's badge
    // says "Zoologist × 2", so a receipt that split them into two identical
    // rows would be the only place in the flow that disagreed. The cart
    // lines underneath stay separate — that is what keeps them two
    // subscriptions — but this is display, and display follows step 1.
    const promo = bxgyConfig();
    const offer = effectiveOffer(promo);
    const giftOn = giftOffered(promo);
    const bundleOn = bundleMode(promo);
    let bucketAGifts = 0;
    const roster = readyExplorers();

    if (term) {
      // Collapsed in first-added order, so a tier keeps the position it
      // was chosen in rather than jumping when a sibling is added to it.
      const tierGroups = [];
      state.explorers.forEach((x) => {
        const key = String(x.variantId);
        const found = tierGroups.find((g) => g.key === key);
        if (found) found.n += 1;
        else tierGroups.push({ key, n: 1, explorer: x });
      });

      tierGroups.forEach((group) => {
        const x = group.explorer;
        const alloc = allocFor(x);
        if (!alloc) return;
        total += alloc.price * group.n;
        boxTotal += alloc.price * group.n;
        for (let i = 0; i < group.n; i += 1) boxPrices.push(alloc.price);
        const monthlyAlloc = allocationsFor(boxData, x.variantId).monthly;
        fullPrice += (monthlyAlloc ? monthlyAlloc.price : alloc.price / term.months) * term.months * group.n;

        lines.push({
          img: config.boxImage,
          // One box reads as the plan it is, exactly as it always has. Two
          // or more have to be told apart, so the tier leads and the plan
          // moves to the fine print below the total (it is shared anyway).
          name: roster.length > 1 ? esc(x.title) : esc(term.label),
          sub: roster.length > 1 ? term.label : '',
          // The challenger can empty its cart: the bin on the only row is
          // live, and the receipt shows an empty state with the way back.
          // Control's receipt has no controls at all, so the flag only ever
          // reads false there.
          qty: { kind: 'tier', id: x.variantId, n: group.n, canRemove: rosterOn() },
          price: formatMoney(alloc.price * group.n),
        });

        // Gifts are earned per box. Bucket A's single plush is identical
        // for every explorer, so it collapses into one × N row; bucket B's
        // bundle differs by tier, so each explorer gets its own group.
        if (giftOn && planQualifies(alloc.planId, offer)) {
          if (bundleOn) {
            const rows = tierBundleRows(offer, x.variantId);
            const value = bundleValueOf(rows) * group.n;
            if (rows.length) {
              lines.push({
                cls: 'sf__cart-item--bundle-head',
                noThumb: true,
                name: esc(bundleCopy(config.bundleTitle, config.bundleTitleOne, rows.length) || 'Free past boxes'),
                sub: `🎁 ${group.n > 1 ? `${group.n} FREE Gifts` : 'FREE Gift'}${roster.length > 1 ? ` · ${esc(x.title)}` : ''}`,
                price: '',
              });
              rows.forEach((row) => {
                lines.push({ cls: 'sf__cart-item--bundle-row', img: row.image, zoomRow: row, name: escPastBoxName(row.name), sub: '', price: '' });
              });
              lines.push({ cls: 'sf__cart-item--bundle-total', noThumb: true, name: 'Bundle value', price: value > 0 ? `<s>${formatMoney(value)}</s> FREE` : 'FREE' });
            }
          } else {
            bucketAGifts += group.n;
          }
        }
      });
      // The undo strip for a removed tier sits below the rows, since a
      // grouped row no longer has a single index to slot back into.
      if (state.removed) {
        lines.push({ undoFor: state.removed.explorers[0], undoCount: state.removed.count || 1 });
      }
    }

    // The family discount Recharge applies at checkout, mirrored here so
    // the total is the number the card will be charged. Percent of the
    // plan price, per Recharge's "in addition to subscribe and save".
    const familyPct = familyPercentFor(totalBoxes());
    const familyOff = familyOffFor(boxPrices, familyPct);
    if (familyOff > 0) {
      total -= familyOff;
      lines.push({
        cls: 'sf__cart-item--discount',
        noThumb: true,
        name: esc((FAMILY_COPY.receipt || '').replace('__PCT__', String(familyPct))),
        price: `−${formatMoney(familyOff)}`,
      });
    }

    // One receipt line per picked size, plus one for any explorer still
    // without a size.
    const vestN = vestCount();
    if (vestN > 0) {
      const vestLine = (sub, n) => ({
        img: VEST.image,
        name: esc(VEST.title || VEST.name),
        sub,
        cls: 'sf__cart-item--gift sf__cart-item--vest',
        qty: { kind: 'gift', id: null, n, locked: true },
        price: VEST.price ? `<s>${formatMoney(VEST.price * n)}</s> FREE` : 'FREE',
      });
      let picked = 0;
      vestLines().forEach(({ variant, qty }) => {
        picked += qty;
        lines.push(vestLine(`🎁 FREE Gift · Size ${esc(variant.label)}`, qty));
      });
      if (vestN > picked) lines.push(vestLine('Pick a size above', vestN - picked));
    }

    if (bucketAGifts > 0) {
      lines.push({
        img: config.giftImage,
        name: esc(config.giftTitle),
        sub: '🎁 FREE Gift',
        // Shown, not offered: the promo decides how many gifts the order
        // earns, so the stepper renders in its disabled state rather than
        // disappearing — the quantity is still a fact worth reading, and a
        // control that vanishes reads as "this row is different" without
        // saying why.
        cls: 'sf__cart-item--gift',
        qty: { kind: 'gift', id: null, n: bucketAGifts, locked: true },
        price: `<s>${formatMoney(config.giftPrice * bucketAGifts)}</s> FREE`,
      });
    }

    if (siblingQty > 0 && config.siblingPrice) {
      const sibTotal = config.siblingPrice * siblingQty;
      total += sibTotal;
      fullPrice += sibTotal;
      const qtyLabel = siblingQty > 1 ? ` × ${siblingQty}` : '';
      lines.push({ img: config.siblingImage, name: `Sea Otter Plush${qtyLabel}`, sub: 'One-time add-on', price: formatMoney(sibTotal) });
    }
    state.addons.forEach((addon, variantId) => {
      const n = Math.max(1, addon.qty || 1);
      total += addon.price * n;
      fullPrice += addon.price * n;
      // Flash-sale boxes (compareAt set) get the struck-through original
      // price beside the sale price and a "One-time purchase" sub-label;
      // regular add-ons stay as a plain price with no sub.
      const hasCompare = addon.compareAt && addon.compareAt > addon.price;
      lines.push({
        img: addon.image,
        tag: hasCompare ? '🔥 Flash Sale' : '',
        name: esc(addon.title),
        sub: hasCompare ? 'One-time purchase' : '',
        qty: { kind: 'addon', id: variantId, n, canRemove: true },
        price: hasCompare
          ? `<s>${formatMoney(addon.compareAt * n)}</s> ${formatMoney(addon.price * n)}`
          : formatMoney(addon.price * n),
      });
    });

    // A cart-drawer row: thumb, name, then a quantity control and a bin on
    // their own line. The gift renders the same control disabled rather
    // than omitting it — the quantity is still worth reading, and a row
    // that simply lacks the control reads as different without saying why.
    const qtyHtml = (q) => {
      // Control's receipt is prod's receipt: name, sub-label, price. No
      // stepper, no bin — including on the gift row, which in control can
      // only ever be one.
      if (!q || !rosterOn()) return '';
      const locked = Boolean(q.locked);
      const attr = q.kind === 'tier' ? 'data-row-tier' : 'data-row-addon';
      const idAttr = locked ? '' : ` ${attr}="${esc(String(q.id))}"`;
      const minusOff = locked || (q.kind === 'tier' && !q.canRemove && q.n <= 1);
      return `<span class="sf__cart-qty">` +
        `<span class="sf__stepper sf__stepper--row${locked ? ' is-locked' : ''}">` +
          `<button type="button" class="sf__step-btn" data-row-minus${idAttr}${minusOff ? ' disabled' : ''} aria-label="Remove one">−</button>` +
          `<span class="sf__step-count sf__step-count--tight">${q.n}</span>` +
          `<button type="button" class="sf__step-btn" data-row-plus${idAttr}${locked ? ' disabled' : ''} aria-label="Add one">+</button>` +
        `</span>` +
        (locked || !q.canRemove
          ? ''
          : `<button type="button" class="sf__cart-bin" data-row-remove${idAttr} data-row-kind="${q.kind}" aria-label="Remove from order">` +
            `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M10 4h4M9 7v12M15 7v12M6 7l1 13h10l1-13" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>` +
            `</button>`) +
        `</span>`;
    };

    linesEl.innerHTML = lines.map((it) => {
      // A removed row's place in the list, still reversible.
      if (it.undoFor) {
        const who = esc(it.undoFor.title);
        const n = it.undoCount > 1 ? ` × ${it.undoCount}` : '';
        return `<li class="sf__cart-item sf__cart-item--undo"><span>Removed ${who}${n}</span><button type="button" class="sf__cart-undo" data-kid-undo>Undo</button></li>`;
      }
      const thumb = it.noThumb
        ? ''
        : it.img
          ? zoomThumbHtml(it.zoomRow, `<img class="sf__cart-thumb" src="${it.img}" alt="" width="52" height="52" loading="lazy">`, false)
          : '<span class="sf__cart-thumb sf__cart-thumb--ph" aria-hidden="true">🦁</span>';
      const subrow = (it.tag || it.sub)
        ? `<span class="sf__cart-subrow">${it.tag ? `<span class="sf__flash-tag">${it.tag}</span>` : ''}${it.sub ? `<span class="sf__cart-sub">${it.sub}</span>` : ''}</span>`
        : '';
      return `<li class="sf__cart-item${it.cls ? ` ${it.cls}` : ''}">${thumb}<span class="sf__cart-info"><span class="sf__cart-name">${it.name}</span>${subrow}${qtyHtml(it.qty)}</span><span class="sf__cart-price">${it.price}</span></li>`;
    }).join('');
    totalEl.textContent = formatMoney(total);
    root.querySelector('[data-totalbar-amount]').textContent = formatMoney(total);
    // Struck through only when there is a saving to show: a monthly plan
    // for one child costs exactly its month-to-month price.
    // Challenger only: control's total bar is production's, a single figure.
    const showWas = rosterOn() && fullPrice > total;
    root.querySelectorAll('[data-totalbar-was], [data-summary-was]').forEach((el) => {
      el.textContent = showWas ? formatMoney(fullPrice) : '';
      el.hidden = !showWas;
    });
    totalbarRow.hidden = total === 0 || state.step < 2;
    // The badge row reads the selected plan and the gift, so it repaints here
    // as well as from paintTiers — a plan change never touches the roster.
    paintFamilySavings();

    if (term) {
      // Two facts, in the order they are wanted: what today's money buys,
      // then when it happens again. The plan's name is not repeated here —
      // it is already on every row above — and the old line called the plan
      // "for each box", which was wrong: a box does not renew, a child's
      // subscription does.
      const many = roster.length > 1;
      const prepaid = term.months === 1
        ? ''
        : `Prepaid for ${term.months} boxes${many ? ' for each child' : ''}. `;
      const renews = many
        ? `Each subscription renews every ${term.months === 1 ? 'month' : `${term.months} months`} until canceled.`
        : `Renews every ${term.months === 1 ? 'month' : `${term.months} months`} until canceled.`;
      fineEl.textContent = `${prepaid}${renews}`;
    } else {
      fineEl.textContent = '';
    }

    // Nothing in the cart and nothing to add up: the rows, the total, the
    // fine print and the delivery promise are all replaced by one line and
    // the way back to step 1. Challenger only — control's roster cannot be
    // emptied, so the card is never in this state there.
    const isEmpty = rosterOn() && state.explorers.length === 0;
    if (emptyEl) emptyEl.hidden = !isEmpty;
    summaryEl.classList.toggle('is-empty', isEmpty);

    canCheckout = !!(term && roster.length > 0);
    updateContinueUI();
  }

  // ---------- The explorer roster ----------
  // The roster lives on step 1: the age cards ARE the roster, so a second
  // child is another age rather than a second trip through the flow. 89% of
  // households buying two boxes pick two different tiers, which makes
  // "one of these and one of those" the common gesture and a per-tier count
  // the natural control.
  //
  // Every mutation goes through `setTierCount` and every read of the DOM is
  // `paintTiers` rendering `state.explorers` from scratch. Nothing patches a
  // count in place — the promotion paths are exactly where an incremental
  // sync goes wrong.
  const REMOVE_UNDO_MS = 6000;
  const tierEls = [...root.querySelectorAll('[data-tier]')];
  const rosterStatusEl = root.querySelector('[data-roster-status]');

  function tierCount(variantId) {
    return state.explorers.filter((x) => String(x.variantId) === String(variantId)).length;
  }
  // The roster's own size, deliberately not `readyExplorers()`: that one
  // also requires live price data, which arrives after this script runs, so
  // counting it here would leave the quantity controls hidden until the
  // fetch landed and then never repaint them.
  function totalBoxes() {
    return state.explorers.length;
  }
  // 2nd, 3rd, 4th, 5th — the roster is capped at 5, so nothing past that.
  function ordinal(n) {
    return ['', '1st', '2nd', '3rd', '4th', '5th'][n] || `${n}th`;
  }
  function tierTitleFor(variantId) {
    const el = tierEls.find((t) => t.dataset.variantId === String(variantId));
    return el ? el.dataset.variantTitle : '';
  }

  // Announced on its own rather than by making the receipt a live region:
  // the receipt is re-rendered wholesale, so it would read the entire cart
  // back on every change.
  function announceRoster(message) {
    if (!rosterStatusEl) return;
    rosterStatusEl.textContent = '';
    requestAnimationFrame(() => { rosterStatusEl.textContent = message; });
  }
  function boxesPhrase(n) {
    return n === 1 ? '1 box in your order.' : `${n} boxes in your order.`;
  }

  // Control is a pick-one group; the challenger is a multi-select where each
  // card carries its own count. Applied once per paint so a late assignment
  // corrects it too.
  function paintTierSemantics() {
    const multi = rosterOn();
    const group = root.querySelector('[data-tiers]');
    if (group) {
      group.setAttribute('role', multi ? 'group' : 'radiogroup');
      group.setAttribute('aria-label', multi ? 'An age for each child' : 'Age group');
    }
    tierEls.forEach((tier) => {
      const main = tier.querySelector('[data-age-option]');
      if (!main) return;
      if (multi) {
        main.removeAttribute('role');
        main.removeAttribute('aria-checked');
      } else {
        main.setAttribute('role', 'radio');
        main.removeAttribute('aria-pressed');
      }
    });
  }

  // The savings line under the age grid. Animated by class, not by the
  // `hidden` attribute, which cannot transition — and the text is cleared
  // only once the collapse has run, or the line would blank out before it
  // had finished shrinking. Re-entrant: a fast second change cancels the
  // pending clear rather than wiping copy that is on its way in.
  const familyEl = root.querySelector('[data-family-savings]');
  const familyLineEl = root.querySelector('[data-family-line]');
  let familyClearTimer = null;
  // Declarations, not arrow consts: buildPlanCards runs during init, which
  // reaches this file's bottom half before these lines have been evaluated.
  function joinChips(chips) {
    return chips.join('<span class="sf__chip-plus" aria-hidden="true">+</span>');
  }

  // `--discount` rides along deliberately: it is what resets the base chip's
  // uppercase and letter-spacing, so a chip without it reads "FREE GIFT".
  function giftChipHtml(n) {
    return `<span class="sf__chip sf__chip--discount sf__chip--gift">\u{1F381} FREE Gift${n > 1 ? ` \u00D7 ${n}` : ''}</span>`;
  }

  // What the bar carries under the total: the plan's per-box discount and the
  // multibox rate, with no `+` separators — the plan cards use those to say
  // "these stack", which is already established by the time the bar is read.
  // The gift badge is a plan card's alone; beside a total the gift does not
  // change, it read as part of the arithmetic.
  //
  // The plan badge waits for step 2. `state.term` has a default from the first
  // frame, so naming a plan's discount on the age step would advertise a plan
  // nobody has chosen.

  // Keyed so a repaint can tell a chip that was already there from one that has
  // just been earned, and animate only the new one.
  function familyBadges() {
    const pct = familyPercentFor(totalBoxes());
    const chips = [];
    if (pct > 0) {
      chips.push([`multibox:${pct}`, `<span class="sf__chip sf__chip--discount sf__chip--family">${pct}% multibox discount</span>`]);
    }
    if (state.step < 2) return chips;

    const term = TERMS.find((t) => t.key === state.term);
    const alloc = currentBoxAlloc();
    const allocs = boxData && state.ageVariantId ? allocationsFor(boxData, state.ageVariantId) : null;
    const monthly = allocs ? allocs.monthly : null;
    // Same figure the plan card carries, and per-box either way: every tier
    // discounts the same percentage for a given term.
    const offPct = alloc && monthly && term && term.months > 1
      ? Math.round((1 - alloc.price / term.months / monthly.price) * 100)
      : 0;
    // After the multibox chip, not before it: the multibox rate is already on
    // screen from step 1, and inserting ahead of it would shove it sideways
    // exactly as the new chip animates in.
    if (offPct > 0) {
      chips.push([`plan:${offPct}`, `<span class="sf__chip sf__chip--discount">${offPct}% off each box</span>`]);
    }

    // Read from the promo exactly as the plan card does, bundle rows included —
    // bucket B only claims a gift it can actually picture.
    const promo = bxgyConfig();
    const offer = effectiveOffer(promo);
    const bundleOn = bundleMode(promo);
    const boxes = totalBoxes();
    const hasGift = !!alloc
      && giftOffered(promo)
      && planQualifies(alloc.planId, offer)
      && (!bundleOn || tierBundleRows(offer, state.ageVariantId).length > 0);
    // Counted in subscriptions, not gift items: one gift per child reads
    // straight off the roster, where bucket B's three-box bundle would not.
    // The bar shares its row with two other badges and the Continue button, so
    // the gift is just its emoji and how many — the plan card above still spells
    // it out in full.
    if (hasGift) {
      // One box leaves room to say it in words; a multi-box row is sharing its
      // line with two other badges, so the gift shrinks to its emoji and count.
      const giftLabel = boxes > 1 ? `\u{1F381} \u00D7 ${boxes}` : '\u{1F381} FREE Gift';
      chips.push([`gift:${boxes}`, `<span class="sf__chip sf__chip--discount sf__chip--gift">${giftLabel}</span>`]);
    }
    return chips;
  }

  // The bar's row never wraps: it scales the badges down until they fit. A
  // wrapped row grew the bar by a whole line and pushed the page's last line of
  // copy under it, and the three badges are already the width of a phone.
  // Two passes, because shrinking the type shrinks the padding with it, so the
  // first estimate always leaves a little room on the table.
  function fitFamilyBadges() {
    if (!familyLineEl) return;
    // The row itself is a nowrap flex line that grows past its container, so
    // its own clientWidth reports the content width and always "fits". The
    // clipped wrapper is what the shopper can actually see.
    const avail = familyEl ? familyEl.clientWidth : familyLineEl.clientWidth;
    if (!avail) return;
    let scale = 1;
    for (let pass = 0; pass < 2; pass += 1) {
      familyLineEl.style.setProperty('--sf-badge-scale', String(scale));
      const kids = Array.from(familyLineEl.children);
      if (!kids.length) return;
      const gaps = (kids.length - 1) * 0.35 * 16 * scale;
      const need = kids.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0) + gaps;
      if (need <= avail) break;
      // 0.6 is the floor: below it the chips stop being legible and a wrapped
      // row would be the better failure.
      scale = Math.max(0.6, scale * (avail / need));
    }
    familyLineEl.style.setProperty('--sf-badge-scale', String(scale));
  }

  // Which chips were on screen at the last paint, so only the ones that just
  // arrived carry the entry animation. Without it the whole row re-animates on
  // every roster change, which reads as a flicker rather than a reward.
  let shownChipKeys = [];
  // What was last written, kept separately from the live innerHTML: marking a
  // badge as entering edits its class attribute, so reading innerHTML back
  // would never match and every paint would replace the row — destroying the
  // node mid-animation, which is why the badge never visibly popped.
  let shownChipHtml = '';
  function paintFamilySavings() {
    if (!familyEl || !familyLineEl) return;
    clearTimeout(familyClearTimer);
    const chips = rosterOn() ? familyBadges() : [];
    const keys = chips.map(([key]) => key);
    if (chips.length) {
      // Opening from nothing, the row's own 0fr→1fr expansion runs for 0.28s;
      // a badge popping underneath it reads as a slide rather than a pop. Hold
      // the badge until the row has made space. Once the row is open there is
      // nothing to wait for, so the delay is zero.
      familyLineEl.style.setProperty('--sf-chip-delay', familyEl.classList.contains('is-on') ? '0s' : '0.24s');
      // The markup carries no animation class: paintFamilySavings runs twice per
      // change (paintTiers and updateSummary both call it), and baking the class
      // into the string meant the second pass rewrote it away before a frame had
      // been drawn — the badge never animated at all. Rendered first, marked
      // second, and the mark survives because the second pass finds identical
      // markup and leaves the DOM alone.
      const html = chips.map(([, chip]) => chip).join('');
      if (shownChipHtml !== html) {
        familyLineEl.innerHTML = html;
        shownChipHtml = html;
      }
      // The key carries the value, so a badge animates both when it is first
      // earned and when its number changes — picking a shorter plan drops the
      // per-box rate, and the badge should show that rather than quietly
      // swapping a digit.
      Array.from(familyLineEl.children).forEach((el, i) => {
        if (shownChipKeys.includes(keys[i])) return;
        el.classList.add('is-entering');
        el.addEventListener('animationend', () => el.classList.remove('is-entering'), { once: true });
      });
      shownChipKeys = keys;
      familyEl.classList.add('is-on');
      fitFamilyBadges();
      return;
    }
    shownChipKeys = [];
    if (!familyEl.classList.contains('is-on') && !shownChipHtml) return;
    familyEl.classList.remove('is-on');
    shownChipHtml = '';
    familyClearTimer = setTimeout(() => { familyLineEl.innerHTML = ''; }, 300);
  }

  function paintTiers() {
    paintTierSemantics();
    paintFamilySavings();
    const total = totalBoxes();
    tierEls.forEach((tier) => {
      const n = tierCount(tier.dataset.variantId);
      const main = tier.querySelector('[data-age-option]');
      tier.classList.toggle('is-selected', n > 0);
      if (main) {
        if (rosterOn()) main.setAttribute('aria-pressed', String(n > 0));
        else main.setAttribute('aria-checked', String(n > 0));
      }
      const qty = tier.querySelector('[data-tier-qty]');
      if (!qty) return;
      // Both controls stay hidden until a first tier is chosen, so the
      // 93% buying one box never meet them.
      // Control renders the markup but never reveals it — the age cards
      // stay the pick-one group they have always been.
      qty.hidden = !rosterOn() || total < 1;
      const add = qty.querySelector('[data-tier-add]');
      const stepper = qty.querySelector('[data-tier-stepper]');
      if (add) {
        add.hidden = n > 0 || total >= effectiveMax();
        const label = add.querySelector('[data-add-label]');
        // "Add a 2nd child" while one box is in the order, "a 3rd" while
        // two are, and so on — so the button says what tapping it does
        // rather than restating the age above it.
        //
        // With a family rate for the roster this tap would produce, the
        // button names it too: the discount is the reason to press it, and
        // it is worth saying at the point of the decision rather than only
        // in the line below the grid. No rate configured for that count
        // and the button keeps the plain wording — never "save 0%".
        const nextOrd = ordinal(total + 1);
        const nextPct = familyPercentFor(total + 1);
        const fill = (tpl) => (tpl || '')
          .replace('__ORD__', nextOrd)
          .replace('__PCT__', String(nextPct))
          .replace('__TIER__', tier.dataset.variantTitle);
        // addSaving carries an <em>; ordinal and percent are theme-generated, not user input.
        if (label) {
          if (nextPct > 0) label.innerHTML = fill(FAMILY_COPY.addSaving);
          else label.textContent = fill(FAMILY_COPY.add);
        }
        add.setAttribute('aria-label', fill(nextPct > 0 ? FAMILY_COPY.addSavingAria : FAMILY_COPY.addAria));
      }
      if (stepper) stepper.hidden = n < 1;
      const count = qty.querySelector('[data-tier-count]');
      if (count) count.textContent = `${n} ${n === 1 ? 'child' : 'children'}`;
      const minus = qty.querySelector('[data-tier-minus]');
      const plus = qty.querySelector('[data-tier-plus]');
      // The step can never be emptied, and the cap is a hard ceiling.
      if (minus) minus.disabled = total <= 1;
      if (plus) plus.disabled = total >= effectiveMax();
    });
  }

  // The one writer. `delta` of +1 adds a box of this tier, -1 removes the
  // most recently added one, and the whole page is repainted from the
  // resulting roster.
  function setTierCount(variantId, delta, { announce = true } = {}) {
    const total = totalBoxes();
    if (delta > 0 && total >= effectiveMax()) return;
    // Control's floor is one box — there is no way back from an empty flow
    // there. The challenger's receipt has one, so its floor is zero and the
    // last row's minus is a real control.
    if (delta < 0 && total <= (rosterOn() ? 0 : 1)) return;
    commitRemoval();
    const title = tierTitleFor(variantId);
    if (delta > 0) {
      const el = tierEls.find((t) => t.dataset.variantId === String(variantId));
      state.explorers.push(newExplorer({
        variantId,
        title,
        tierIndex: el ? el.dataset.tierIndex : '',
      }));
      state.galleryTierIndex = el ? el.dataset.tierIndex : '';
      if (announce) announceRoster(`${title} added. ${boxesPhrase(totalBoxes())}`);
      analytics.explorers('add');
    } else {
      for (let i = state.explorers.length - 1; i >= 0; i -= 1) {
        if (String(state.explorers[i].variantId) === String(variantId)) {
          state.explorers.splice(i, 1);
          break;
        }
      }
      if (announce) announceRoster(`${title} removed. ${boxesPhrase(totalBoxes())}`);
      analytics.explorers('remove');
    }
    rosterChanged();
  }

  // Picking a tier when exactly one box is in the order replaces it — the
  // radio behaviour the flow has always had, and the reason an ordinary card
  // tap can never quietly add a second box. Above one box the card body is a
  // toggle for its whole tier — tapping a selected card removes every child
  // on it — while the steppers still handle counts within a tier.
  function pickTier(variantId) {
    const total = totalBoxes();
    if (total > 1) {
      // A built roster: the card is a toggle. Tapping a tier that is in the
      // order takes every child on it out; tapping one that isn't adds one.
      const n = tierCount(variantId);
      if (n > 0) {
        commitRemoval();
        state.explorers = state.explorers.filter((x) => String(x.variantId) !== String(variantId));
        announceRoster(`${tierTitleFor(variantId)} removed. ${boxesPhrase(totalBoxes())}`);
        analytics.explorers('remove');
        rosterChanged();
      } else {
        setTierCount(variantId, 1);
      }
      return;
    }
    if (tierCount(variantId) > 0) return;
    commitRemoval();
    const el = tierEls.find((t) => t.dataset.variantId === String(variantId));
    state.explorers = [newExplorer({
      variantId,
      title: tierTitleFor(variantId),
      tierIndex: el ? el.dataset.tierIndex : '',
    })];
    state.galleryTierIndex = el ? el.dataset.tierIndex : '';
    rosterChanged();
  }

  // Out of the empty cart: back to step 1 with the default tier in the
  // order again. Shared by the receipt's "Add a subscription" and the bar's
  // Back button.
  function restartFromEmpty() {
    // If a traversal is still in flight the tap is dropped whole, rather
    // than refilling the cart while the shopper stays on the review step.
    if (!go(1)) return;
    if (defaultTierEl) pickTier(defaultTierEl.dataset.variantId);
    announceRoster(`${tierTitleFor(defaultTierEl ? defaultTierEl.dataset.variantId : '')} added. ${boxesPhrase(totalBoxes())}`);
  }

  // Everything a roster change has to touch, in one place.
  function rosterChanged() {
    // Every extra hangs off having a subscription, so emptying the roster
    // takes them with it rather than leaving a plush to be checked out on
    // its own. Challenger only, since control's roster can never be empty.
    if (rosterOn() && state.explorers.length === 0 && state.addons.size) {
      state.addons.clear();
    }
    paintAddons();
    const first = primaryExplorer();
    if (first) {
      state.ageVariantId = first.variantId;
      state.ageTitle = first.title;
    } else {
      // Cleared, not left over: Back to the plan step with an empty cart
      // would otherwise price cards for a tier that is no longer in it.
      state.ageVariantId = null;
      state.ageTitle = '';
    }
    paintTiers();
    // The gallery follows the box most recently added, not the first in the
    // roster, so adding a Zoologist shows the Zoologist box. When that tier
    // is removed again it falls back to the newest box still in the order.
    const galleryStillIn = state.explorers.some((e) => e.tierIndex === state.galleryTierIndex);
    if (!galleryStillIn) {
      const newest = state.explorers[state.explorers.length - 1];
      state.galleryTierIndex = newest ? newest.tierIndex : null;
    }
    showTierGallery(state.galleryTierIndex || null);
    if (boxData) buildPlanCards();
    updateContinueUI();
    updateSummary();
  }
  // Exposed so the product-data fetch can repaint once prices exist.
  rosterRepaint = rosterChanged;

  function commitRemoval() {
    if (!state.removed) return;
    clearTimeout(state.removed.timer);
    state.removed = null;
    updateSummary();
  }

  // The bin on a receipt row takes the whole tier, because the row IS the
  // tier: a bin that removed one of two identical children would leave the
  // row looking untouched apart from its number. A few seconds of undo
  // instead of a confirm. Pure state — the Shopify cart is composed from
  // scratch at Checkout, so no removal here can strand a paid-for gift.
  function removeTier(variantId) {
    const key = String(variantId);
    const going = state.explorers.filter((x) => String(x.variantId) === key);
    if (!going.length) return;
    // Taking the last group empties the cart. Control has no way back from
    // that, so it still refuses; the challenger allows it and offers the
    // way back in the receipt itself, which is also why it needs no undo
    // strip — an empty cart cannot be mistaken for a small edit.
    const emptying = going.length >= state.explorers.length;
    if (emptying && !rosterOn()) return;
    commitRemoval();
    const index = state.explorers.findIndex((x) => String(x.variantId) === key);
    state.explorers = state.explorers.filter((x) => String(x.variantId) !== key);
    if (emptying) {
      rosterChanged();
      announceRoster(`${going[0].title}${going.length > 1 ? ` × ${going.length}` : ''} removed. Your cart is empty.`);
      analytics.explorers('remove');
      return;
    }
    state.removed = {
      explorers: going,
      count: going.length,
      index,
      timer: setTimeout(() => {
        state.removed = null;
        updateSummary();
      }, REMOVE_UNDO_MS),
    };
    rosterChanged();
    announceRoster(`${going[0].title}${going.length > 1 ? ` × ${going.length}` : ''} removed. ${boxesPhrase(totalBoxes())} Undo is available for a few seconds.`);
    analytics.explorers('remove');
  }

  function undoRemoval() {
    if (!state.removed) return;
    const { explorers, index, timer, count } = state.removed;
    clearTimeout(timer);
    state.removed = null;
    state.explorers.splice(Math.min(index, state.explorers.length), 0, ...explorers);
    rosterChanged();
    announceRoster(`${explorers[0].title}${count > 1 ? ` × ${count}` : ''} is back. ${boxesPhrase(totalBoxes())}`);
  }

  // Row actions are delegated: the summary list is re-rendered from state
  // on every change, so per-button listeners would be rebound constantly.
  root.querySelector('[data-summary-lines]').addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const bin = target.closest('[data-row-remove]');
    if (bin) {
      if (bin.dataset.rowKind === 'tier') return removeTier(bin.dataset.rowTier);
      return setAddonQty(bin.dataset.rowAddon, -Infinity);
    }
    const minus = target.closest('[data-row-minus]');
    if (minus) {
      if (minus.dataset.rowTier) return setTierCount(minus.dataset.rowTier, -1);
      if (minus.dataset.rowAddon) return setAddonQty(minus.dataset.rowAddon, -1);
      return;
    }
    const plus = target.closest('[data-row-plus]');
    if (plus) {
      if (plus.dataset.rowTier) return setTierCount(plus.dataset.rowTier, 1);
      if (plus.dataset.rowAddon) return setAddonQty(plus.dataset.rowAddon, 1);
      return;
    }
    if (target.closest('[data-kid-undo]')) undoRemoval();
  });

  // The way out of the empty cart: back to step 1 with the same tier a
  // fresh load would have selected, so the shopper lands on a flow that is
  // already answerable rather than one with nothing chosen. Seeded through
  // `pickTier` and `defaultTierEl` — the init path — rather than a second
  // copy of the ?tier= resolution.
  const emptyAddBtn = root.querySelector('[data-summary-empty-add]');
  if (emptyAddBtn) emptyAddBtn.addEventListener('click', restartFromEmpty);

  if (defaultTierEl) pickTier(defaultTierEl.dataset.variantId);

  tierEls.forEach((tier) => {
    const id = tier.dataset.variantId;
    const main = tier.querySelector('[data-age-option]');
    if (main) main.addEventListener('click', () => pickTier(id));
    const add = tier.querySelector('[data-tier-add]');
    if (add) add.addEventListener('click', () => setTierCount(id, 1));
    const plus = tier.querySelector('[data-tier-plus]');
    if (plus) plus.addEventListener('click', () => setTierCount(id, 1));
    const minus = tier.querySelector('[data-tier-minus]');
    if (minus) minus.addEventListener('click', () => setTierCount(id, -1));
  });

  // ---------- Gift safety net ----------
  // The promo app's fetch interceptor adds the free gift, but its own
  // cart call can silently lose a race with the box add. Mirror the
  // app's rules from its embedded config and top up the gift if it's
  // missing. Best-effort only — never blocks checkout.
  // One gift line. A 422 is Shopify saying it can't sell that variant right
  // now — sold out — which no amount of retrying fixes, so it returns
  // immediately and the caller moves on to the next candidate. Every other
  // failure, a dropped connection included, is read as the cart being
  // briefly locked and retried on the same backoff as the extras add. The
  // network case is caught here rather than left to throw, so one bad
  // request can't take the rest of the candidate walk down with it.
  async function addGiftLine(variantId, quantity = 1) {
    return addGiftLines([{ id: variantId, quantity }]);
  }

  // One or more gift lines, in a single request.
  //
  // `/cart/add.js` takes an `items` array, and using it is the point: one add
  // per box meant three cart writes landing back to back on the bundle arm,
  // and from the second write on Shopify answered 429 — so the shopper reached
  // checkout with one box of the three they were shown, and which two they
  // lost was just their position in the list. Measured on live orders: 3 of 34
  // qualifying bucket-B orders between 25 Aug and 4 Sep 2026.
  //
  // The request is atomic, so one variant Shopify won't sell right now (422)
  // fails it for all of them; the caller falls back to adding them one at a
  // time so the rest still arrive. Every other failure is read as the cart
  // being briefly locked and retried on the same backoff as the extras add.
  // The network case is caught here rather than left to throw, so one bad
  // request can't take the rest of the walk down with it.
  async function addGiftLines(lines) {
    const items = lines.map(({ id, quantity }) => ({
      id,
      quantity: quantity == null ? 1 : quantity,
      properties: { _bxgy: 'true' },
    }));
    if (!items.length) return false;

    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (res.ok) return true;
        if (res.status === 422) return false;
      } catch (e) {
        /* connection failed — same as any other retryable attempt */
      }
    }
    return false;
  }

  async function ensureGifts() {
    try {
      // The embed decides who gets a gift, whenever it is on the page.
      // `__BXGY_LOADED__` is the promo app's cart embed saying its script
      // ran; from that point it is the authority on eligibility. On the
      // sessions it takes over it also sets `__BXGY_ACTIVE__`, installs its
      // interceptor, and adds and reconciles the gift lines itself. On the
      // sessions it skips — the school channel and off-audience utm ids — it
      // adds nothing, and that is the intended answer, not a gap: those
      // exclusions are deliberate targeting rules.
      //
      // So this net stands down on "loaded", not on "active". Reading
      // `__BXGY_ACTIVE__` here meant every session the embed deliberately
      // skipped fell through to this fallback and got the gift anyway —
      // which is how ClassWallet orders kept shipping one despite the
      // carve-out. The fallback exists for pages the embed is absent from,
      // and nothing else.
      //
      // Read at call time, since the embed's script can finish loading
      // after this one has run.
      if (window.__BXGY_LOADED__) return;
      // Null for school-channel visitors too, so no gift is added for them.
      const app = bxgyConfig();
      if (!app || !promoWindowOpen(app)) return;
      const offer = effectiveOffer(app);

      // What the whole cart has earned, summed per gift variant across
      // every explorer. Counted in units rather than as a set of variant
      // ids: two explorers on the same tier earn the same gift twice, and
      // a set would have called that satisfied by one line.
      const want = new Map();
      for (const x of readyExplorers()) {
        const alloc = allocFor(x);
        if (!alloc || !planQualifies(alloc.planId, offer)) continue;
        // The set is keyed by the box variant, so a tier the offer doesn't
        // name earns nothing — same rule the discount applies server-side.
        const set = giftSetFor(offer, x.variantId);
        if (!set) continue;
        // Add what was shown. With display data in hand the bundle panel
        // has already picked this tier's boxes, and adding anything else
        // would put a box in the cart the shopper was never offered — so
        // that list is walked exactly, and a box that sells out between the
        // picture and the add simply doesn't arrive. Without display data
        // there is nothing to honour and this falls back to the raw
        // candidate order, up to `take`. Bucket A always takes that path:
        // it carries no handles, so its walk is one candidate long, exactly
        // as it has always been.
        const shown = displayedGiftGids(set);
        const walk = (shown || set.candidates).filter((gid) => !isVestGid(gid));
        const target = shown ? shown.length : boxTake(set);
        let picked = 0;
        for (const gid of walk) {
          if (picked >= target) break;
          const id = idFromGid(gid);
          if (!id) continue;
          want.set(id, (want.get(id) || 0) + 1);
          picked += 1;
        }
      }
      // The vest is each explorer's chosen size, never a walk of the list.
      vestLines().forEach(({ variant, qty }) => {
        want.set(Number(variant.id), (want.get(Number(variant.id)) || 0) + qty);
      });
      if (!want.size) return;

      // Credit whatever is already there — the app's embed, or an earlier
      // attempt on a retried checkout — so a second pass tops up the
      // shortfall instead of doubling the gift.
      const cart = await (await fetch('/cart.js')).json();
      const have = new Map();
      (cart.items || []).forEach((item) => {
        if (item.properties && item.properties._bxgy === 'true') {
          have.set(item.variant_id, (have.get(item.variant_id) || 0) + item.quantity);
        }
      });

      const missingLines = [];
      for (const [variantId, wanted] of want) {
        const missing = wanted - (have.get(variantId) || 0);
        if (missing <= 0) continue;
        missingLines.push({ id: variantId, quantity: missing });
      }

      // The whole shortfall in one write, and the per-line walk only if that
      // request came back refused — which for this endpoint means one of the
      // boxes can't be sold right now, and the others still can.
      if (missingLines.length > 1 && (await addGiftLines(missingLines))) return;
      for (const { id, quantity } of missingLines) {
        await addGiftLine(id, quantity);
      }
    } catch (e) {
      /* gift is best-effort */
    }
  }

  // ---------- Checkout ----------
  const checkoutError = root.querySelector('[data-checkout-error]');

  // Full-screen confetti celebration (native canvas, no dependencies).
  // Returns false if it was skipped (reduced motion / no canvas support).
  function rainConfetti() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const dpr = window.devicePixelRatio || 1;
    const colors = ['#53a500', '#00552f', '#fcd366', '#d4784c', '#30b0c7', '#badb99'];
    const pieces = [];
    const duration = 3200;
    const start = performance.now();
    canvas.className = 'sf__confetti-canvas';
    document.body.appendChild(canvas);
    const resize = () => {
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    for (let i = 0; i < 200; i += 1) {
      pieces.push({
        x: Math.random() * window.innerWidth,
        y: -60 - Math.random() * window.innerHeight * 1.15,
        w: 6 + Math.random() * 7,
        h: 8 + Math.random() * 12,
        vx: -1.05 + Math.random() * 2.1,
        vy: 2.4 + Math.random() * 3.2,
        rotation: Math.random() * Math.PI,
        spin: -0.18 + Math.random() * 0.36,
        color: colors[i % colors.length],
        opacity: 0.86 + Math.random() * 0.14,
      });
    }
    const draw = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      pieces.forEach((piece) => {
        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.rotation += piece.spin;
        piece.vy += 0.018;
        const fade = elapsed > duration - 1300 ? Math.max(0, (duration - elapsed) / 1300) : 1;
        ctx.save();
        ctx.globalAlpha = piece.opacity * fade;
        ctx.translate(piece.x, piece.y);
        ctx.rotate(piece.rotation);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
        ctx.restore();
      });
      if (elapsed < duration) {
        requestAnimationFrame(draw);
      } else if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
    window.addEventListener('resize', resize, { once: true });
    requestAnimationFrame(draw);
    return true;
  }

  async function runCheckout() {
    const roster = readyExplorers();
    if (!roster.length || !canCheckout || checkoutBusy) return;
    // The picker sits at the top of this step; checkout waits for a size.
    if (vestMissing()) {
      promptVest();
      return;
    }

    // Published before the confetti and before any cart work, deliberately.
    // "Initiated checkout" means the shopper tapped the button — a checkout
    // that then fails to compose must still count, or the last measurable
    // point stays `extras` and the cart → checkout → purchase gap stops
    // being diagnostic for exactly the failure it exists to catch.
    //
    // Not a numbered step: `checkout` has no entry in STEP_NAMES because the
    // flow's numbering is computed at render from which optional steps are
    // on. It is published by name only, which is all the A/B framework's
    // observer reads. That framework owns the beacon and its own dedupe — a
    // shopper who returns after a failure and taps again re-enters this step
    // and is counted as a re-entry, which is the behaviour we want, so
    // nothing here guards the repeat.
    root.setAttribute('data-ab-step', 'checkout');

    checkoutBusy = true;
    continueBtn.disabled = true;
    checkoutError.hidden = true;
    continueLabel.textContent = 'Packing your box…';
    rainConfetti();

    try {
      // Clear the cart first so nothing picked up elsewhere on the site
      // rides along into this subscription checkout.
      // The cart is composed from scratch below, so a clear that did not
      // actually clear would leave the box adds APPENDING to whatever was
      // already there — a second copy of every subscription, charged. The
      // adds retry on a lock; the clear now does too, and a clear that
      // still fails stops the checkout rather than building on top of it.
      let cleared = false;
      for (let attempt = 0; attempt < 3 && !cleared; attempt++) {
        if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        try {
          const clearRes = await fetch('/cart/clear.js', { method: 'POST' });
          cleared = clearRes.ok;
        } catch (e) {
          cleared = false;
        }
      }
      if (!cleared) throw new Error('Could not start a fresh cart. Please try again.');

      // Every subscription goes in one JSON add, ahead of the extras.
      //
      // JSON with an `items` array, not FormData: the promo app's embed
      // reads an intercepted cart add by parsing FormData through
      // `Object.fromEntries` and then taking `body.items || [body]`, so
      // FormData keys of the `items[0][id]` shape are never recognised and
      // no gift sync would fire at all. The JSON form is recognised, and
      // `needsGiftSync` is evaluated across all the items at once, so one
      // request means one awaited reconcile no matter how many boxes are in
      // it — instead of N reconciles racing the cart lock Shopify takes out
      // on each write.
      //
      // The A/B arm rides on this same add rather than a separate
      // /cart/update.js afterwards. `/cart/add.js` accepts a top-level
      // `attributes` object alongside the items, so the arm is recorded by
      // the request that makes the cart exist — nothing to lose to the cart
      // lock the gift add takes out, and nothing left in flight for the
      // redirect to /checkout to cancel. A standalone write had both
      // failure modes and no retry: 2 of 18 orders in the rollout's first
      // day reached checkout with no arm on them at all.
      //
      // Gated on a promo resolving for this market, the same rule the write
      // below and the app embed both apply. A market that is offered
      // neither arm was never in the test, and stamping it anyway files
      // those orders under A or B and dilutes both.
      const boxItems = roster.map((x) => {
        const alloc = allocFor(x);
        // What keeps two boxes two boxes. Shopify merges lines with an
        // identical variant + selling plan + properties into one at
        // quantity 2, which Recharge then models as a single subscription —
        // one a parent cannot cancel, skip or swap for just one child. The
        // key is private (leading underscore) so it never shows to the
        // customer; it exists only to make the lines distinct.
        const properties = {
          _selling_plan: String(alloc.planId),
          _sf_explorer_uid: String(x.uid),
        };
        return { id: Number(x.variantId), quantity: 1, selling_plan: alloc.planId, properties };
      });
      // The vest goes in this same request, tagged as a gift. The promo
      // embed reconciles the gift set once this write has landed, so it
      // credits the chosen size instead of adding a default one; added in a
      // later request it loses that race and a second, default-size vest
      // joins it.
      const vestIds = new Set();
      vestLines().forEach(({ variant, qty }) => {
        vestIds.add(Number(variant.id));
        boxItems.push({ id: Number(variant.id), quantity: qty, properties: { _bxgy: 'true' } });
      });
      const boxBody = { items: boxItems };
      // The clear above wiped every cart attribute, including the A/B
      // framework's `ab_<handle>` arm stamps — and the framework only
      // re-stamps on the theme's cart:update event, which these raw fetches
      // never dispatch, and remembers per session that it already stamped.
      // So the attribution the order needs (and the arm the Family discount
      // Function reads at checkout) is restored here from the framework's
      // own in-page assignment. This writes the framework's values back,
      // not values of our own — the same reason `_promo_bucket` rides along.
      // `_ab_forced` rides along for the same reason: the framework sets it so a
      // QA order placed from a forced-bucket URL can be told apart from real
      // traffic, and the clear above wiped it. Without it a forced checkout is
      // counted as a real visitor's order. Found 4 Sep 2026 on a forced cart.
      const abStamps = Object.fromEntries(
        Object.entries((window.EdzAB && window.EdzAB.buckets) || {}).map(([handle, bucket]) => [`ab_${handle}`, bucket]),
      );
      if (Object.keys(window.EdzABForced || {}).length) abStamps._ab_forced = 'true';
      boxBody.attributes = { ...abStamps, ...(PROMO_TEST_RUNNING && bxgyConfig() ? { _promo_bucket: BUCKET } : {}) };
      if (!Object.keys(boxBody.attributes).length) delete boxBody.attributes;

      // Retried on the same backoff as the extras below. The box add had no
      // retry when it was a single line and the cart was always empty
      // beneath it; with the promo embed's own writes now landing against
      // the same cart, a transient lock is a real failure mode.
      let boxRes = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        try {
          boxRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(boxBody),
          });
        } catch (e) {
          boxRes = null;
          continue;
        }
        if (boxRes.ok) break;
        // 422 is Shopify refusing to sell one of these right now — a box
        // whose bundle components are out of stock at the fulfilling
        // location. Retrying cannot fix it, and the shopper needs to know
        // which explorer to change.
        // A vest size that sold out since the page loaded must not cost the
        // shopper their boxes: drop the vests and try once more.
        if (boxRes.status === 422 && boxBody.items.some((item) => vestIds.has(item.id))) {
          boxBody.items = boxBody.items.filter((item) => !vestIds.has(item.id));
          attempt -= 1;
          continue;
        }
        if (boxRes.status === 422) break;
      }
      if (!boxRes || !boxRes.ok) {
        const err = boxRes ? await boxRes.json().catch(() => ({})) : {};
        throw new Error(err.description || err.message || 'Could not add the boxes to your cart.');
      }

      const items = [];
      if (state.kids > 1 && config.siblingVariantId) {
        items.push({
          id: Number(config.siblingVariantId),
          quantity: state.kids - 1,
        });
      }
      state.addons.forEach((addon, variantId) => {
        items.push({ id: Number(variantId), quantity: Math.max(1, addon.qty || 1) });
      });
      if (items.length) {
        // The cart can briefly lock right after the box + promo-gift adds
        // commit, so retry a few times before surfacing an error.
        let extraRes = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
          extraRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (extraRes.ok) break;
        }
        // `extraRes` stays null if all three attempts threw, so the null
        // is checked before the property — reading `.ok` off it raised a
        // TypeError that surfaced as a generic "something went wrong".
        if (!extraRes || !extraRes.ok) {
          const err = extraRes ? await extraRes.json().catch(() => ({})) : {};
          throw new Error(err.description || err.message || 'Could not add your extras to the cart.');
        }
      }

      await ensureGifts();

      // Which arm of the A/B test this order came from. Attribution only —
      // the discount is authorized server-side from the promo config, so a
      // write that fails costs a data point and never a gift.
      //
      // Fired and left to finish on its own: `keepalive` keeps the request
      // alive across the redirect below, so it adds nothing to the wait
      // before /checkout and still lands on the cart the order is built
      // from. Not awaited and not abortable — a timeout signal would kill
      // exactly the in-flight request keepalive exists to carry. The
      // rejection is swallowed so a failed write can't break checkout.
      //
      // Now a backstop rather than the primary record: the box add above
      // carries `_promo_bucket` itself, so this only matters for a cart
      // that add did not stamp.
      //
      // Skipped when the promo app's cart embed is driving, for the same
      // reason this page stopped adding gift lines then: the embed has
      // already written the attribute, and its interceptor answers any cart
      // write by kicking off a reconcile it doesn't wait for. Firing one
      // more write on the way to /checkout would leave that running against
      // a cart the shopper is already leaving.
      //
      // Also skipped where no promo is published for the shopper's market.
      // The arm only exists inside a promo, so a market the config doesn't
      // claim was never shown either experience and has nothing to
      // attribute — stamping it anyway files those orders under A or B and
      // dilutes both arms with shoppers who saw neither. Same rule the
      // embed applies from the other side: it returns before it installs
      // anything when the promo doesn't resolve, so on those pages neither
      // side writes rather than the theme covering for it.
      //
      // The window is deliberately not consulted, matching the embed again:
      // a visitor inside a published market but outside the promo's dates
      // is still in a test arm and still worth counting.
      if (!window.__BXGY_ACTIVE__ && bxgyConfig()) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { ...abStamps, ...(PROMO_TEST_RUNNING ? { _promo_bucket: BUCKET } : {}) } }),
          keepalive: true,
        }).catch(() => {
          /* attribution is best-effort */
        });
      }

      window.location.href = '/checkout';
    } catch (e) {
      checkoutError.textContent = e.message || 'Something went wrong. Please try again.';
      checkoutError.hidden = false;
      checkoutError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      checkoutBusy = false;
      updateContinueUI();
    }
  }

  // ---------- Load live product data ----------
  async function fetchJsonRetry(url, attempts = 4) {
    for (let i = 0; i < attempts; i++) {
      if (i > 0) await new Promise((resolve) => setTimeout(resolve, 700 * i));
      try {
        const res = await fetch(url);
        if (res.ok) return res.json();
      } catch (e) { /* retry */ }
    }
    throw new Error(`Could not load ${url}`);
  }

  // Prices come from the Storefront API, not /products/<handle>.js. In a
  // converted market the .js endpoint quotes a different exchange rate from
  // the one cart and checkout charge at: on .ca, 19 Oct 2026, it priced the
  // Zoologist 12-month plan at $629 while the cart line was $617 — about $1
  // per box once rounding to the dollar applied, and the sign follows the
  // rates, so it can just as well quote below the charge. The Storefront API
  // returns the cart's figure, the conversion Shopify Payments charges. It
  // needs no token for product reads. The result is reshaped into the .js
  // fields this file reads, and the .js endpoint stays as the fallback so a
  // failed query still shows plans.
  const PRODUCT_QUERY = `query Product($handle: String!, $country: CountryCode!) @inContext(country: $country) {
    product(handle: $handle) {
      title
      featuredImage { url }
      sellingPlanGroups(first: 10) { nodes { name sellingPlans(first: 10) { nodes { id } } } }
      variants(first: 50) { nodes {
        id title availableForSale
        price { amount }
        image { url }
        sellingPlanAllocations(first: 10) { nodes { sellingPlan { id } priceAdjustments { price { amount } } } }
      } }
    }
  }`;
  const gidNumber = (gid) => Number(String(gid).split('/').pop());
  const toCents = (money) => Math.round(Number(money.amount) * 100);

  async function fetchMarketProduct(handle) {
    const res = await fetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: PRODUCT_QUERY, variables: { handle, country: config.country } }),
    });
    if (!res.ok) throw new Error(`Storefront API ${res.status}`);
    const product = (await res.json())?.data?.product;
    if (!product) throw new Error(`No product ${handle}`);
    const variants = product.variants.nodes.map((v) => ({
      id: gidNumber(v.id),
      title: v.title,
      available: v.availableForSale,
      price: toCents(v.price),
      featured_image: v.image ? { src: v.image.url } : null,
      selling_plan_allocations: v.sellingPlanAllocations.nodes.map((a) => ({
        selling_plan_id: gidNumber(a.sellingPlan.id),
        price: toCents(a.priceAdjustments[0].price),
      })),
    }));
    // Recharge makes one plan group per tier, all three named alike. The .js
    // endpoint merges same-named groups and allocationsFor relies on that,
    // reading a term's plan ids from the first group with its name, so the
    // merge is repeated here.
    const groups = new Map();
    product.sellingPlanGroups.nodes.forEach((g) => {
      if (!groups.has(g.name)) groups.set(g.name, { name: g.name, selling_plans: [] });
      g.sellingPlans.nodes.forEach((sp) => groups.get(g.name).selling_plans.push({ id: gidNumber(sp.id) }));
    });
    return {
      title: product.title,
      featured_image: product.featuredImage ? product.featuredImage.url : null,
      price: Math.min(...variants.map((v) => v.price)),
      variants,
      selling_plan_groups: [...groups.values()],
    };
  }

  async function loadProduct(handle, attempts = 4) {
    if (config.country) {
      try {
        return await fetchMarketProduct(handle);
      } catch (e) { /* fall back to the .js endpoint */ }
    }
    return fetchJsonRetry(`/products/${encodeURIComponent(handle)}.js`, attempts);
  }

  // The promo app's #bxgy-config is server-rendered, but it sits far below
  // this script in the document, and this script runs at parse time — so
  // when the IIFE starts, that element does not exist yet. Whether the gift
  // was advertised therefore came down to whether products.js resolved
  // before or after the parser reached it: measured margins of +244ms to
  // -131ms across five loads, which is a coin flip, and the losing side
  // baked hasGift:false into every plan card. Only a tier change rebuilt
  // them, so a shopper who kept the default tier saw no gift all session.
  // Waiting for DOMContentLoaded turns the element's presence into a
  // guarantee. It costs nothing when the fetch was the slower of the two.
  const domReady =
    document.readyState === 'loading'
      ? new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }))
      : Promise.resolve();

  async function loadProducts() {
    try {
      boxData = await loadProduct(config.boxHandle);
      // Before swapping the placeholder out, so the plan area never sits
      // empty while this waits.
      await domReady;
      const loading = root.querySelector('[data-plan-loading]');
      if (loading) loading.remove();
      // Repaints the whole roster, not just the plan cards: the quantity
      // controls and the receipt both read prices that only exist now.
      rosterRepaint();
    } catch (e) {
      root.querySelector('[data-plan-cards]').innerHTML =
        `<p class="sf__loading">We couldn't load plans. <a href="/products/${config.boxHandle}">Subscribe from the product page instead →</a></p>`;
    } finally {
      // After the fetch either way, so the event carries a price when it can
      // and still fires when the fetch failed.
      analytics.viewContent();
    }
  }
  // Bucket B only. Runs alongside loadProducts rather than inside it, so
  // reading three or four product pages can never hold up the plan cards or
  // the ViewContent event. Until it resolves no candidate is known, so the
  // cards carry no bundle; they rebuild once the boxes are in hand.
  async function loadBundleDisplay() {
    await domReady;
    const offer = effectiveOffer();
    if (!offer || !bundleMode()) return;
    await loadCandidateDisplay(offer);
    if (boxData && state.ageVariantId) buildPlanCards();
    updateSummary();
  }

  // Always sync the button state on load, even if the default-select
  // above fell through — otherwise the button keeps its markup `disabled`
  // and there's no way to recover.
  updateContinueUI();
  // goToStep only fires on a transition, so step 1 needs its own entry —
  // without it the funnel has no denominator to measure step 2 against.
  analytics.step(state.step, true);
  loadProducts();
  loadBundleDisplay();
});
